import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import ContractWizardModal from "./ContractWizardModal";
import ContractDetailModal from "./ContractDetailModal";
import BillingDetailModal from "./BillingDetailModal";
import BillingWizardModal from "./BillingWizardModal";
import ManualPaymentModal from "./ManualPaymentModal";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  LayoutDashboard, Pencil, CheckSquare, StickyNote, Mail, X, History,
  Building2, User, UserCheck, Package, Calendar, DollarSign, Clock, ArrowRight,
  TrendingUp, TrendingDown, Check, AlertCircle, CircleDot, Loader2, Plus, Trophy,
  Send, ClipboardList,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ThumbsDown, MoreHorizontal, Eye, Copy, Trash2, Layers,
  Tag, Hash, Folder, Phone, Globe, MapPin, FileText, Briefcase, MessageCircle, Timer,
  Circle, CheckCircle2, BarChart2, ShoppingBag, RotateCcw, Zap, Brain,
  PhoneOutgoing, PhoneIncoming, Mic, FileAudio, Download, ZoomIn, ZoomOut, FileSignature,
  RefreshCw, Receipt, ExternalLink, Upload, Paperclip
} from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { applyMask } from "./PhoneInput";
import SearchSelect from "./SearchSelect";
import CustomFieldsForm from "./CustomFieldsForm";
import { ICON_MAP } from "../lib/iconList";
import { resolveIcon } from "../lib/iconResolver";
import { toast } from "../components/ui/sonner";
import WhatsAppChat from "./WhatsAppChat";
import { fmt, parseBRNumber, fmtDate, fmtDateShort, InlineCell, ProdutoLine, MiniCard, MetricCard, EmpresaCard, UltimoNegocioCard, CompareCard } from "./negocio-detail/NegocioHelpers";
import NegocioDialogs from "./negocio-detail/NegocioDialogs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_ITEM_KEYS = [
  { key: "resumo",    labelKey: "negocios.resume",    icon: LayoutDashboard, available: true  },
  { key: "editar",    labelKey: "negocios.edit",      icon: Pencil,           available: true  },
  { key: "arquivos",  labelKey: "Arquivos",           icon: Folder,           available: true },
  { key: "historico", labelKey: "negocios.history",   icon: History,          available: true  },
  { key: "anotacoes", labelKey: "notes.title",         icon: StickyNote,       available: true },
  { key: "tarefas",   labelKey: "tarefas.title",      icon: CheckSquare,      available: true  },
  { key: "contrato",  labelKey: "Contrato",           icon: FileSignature,    available: true },
  { key: "cobranca",  labelKey: "Cobranças",           icon: Receipt,          available: true },
  { key: "whatsapp",  labelKey: "WhatsApp",            icon: MessageCircle,    available: true  },
  { key: "email",     labelKey: "E-mail",             icon: Mail,             available: true },
];

// Utilities and small UI components imported from ./negocio-detail/NegocioHelpers


export default function NegocioDetailModal({
  open, onClose, negocio, stages, customFields, formLayout, formSections,
  empresas, contatos, produtos, onSaved, negocios = [], pipeline, stageCardLayout = [], stageSla = {}, responsaveis = [], moduleEnabled = { empresas: true, contatos: true, produtos: true }, stageFieldsMap = {}, whatsappUnread = 0, initialTab = null, onNavigate
}) {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const [activeMenu, setActiveMenu] = useState("resumo");
  const { can } = usePermissions();
  const prevOpenRef = useRef(false);
  const prevNegocioIdRef = useRef(null);

  // Reset AI state when navigating to a different deal
  useEffect(() => {
    if (negocio?.id && negocio.id !== prevNegocioIdRef.current) {
      prevNegocioIdRef.current = negocio.id;
      setAiFetched(false);
      setTasks([]);
    }
  }, [negocio?.id]); // eslint-disable-line
  const translateTaskType = (name) => {
    if (!name) return name;
    const key = name.toLowerCase()
      .replace(/-/g, '_').replace(/ã/g, 'a').replace(/ç/g, 'c')
      .replace(/[êé]/g, 'e').replace(/â/g, 'a').replace(/õ/g, 'o').replace(/\s/g, '_');
    const translated = t(`taskTypes.${key}`);
    return translated !== `taskTypes.${key}` ? translated : name;
  };
  // Build translated menu items inside the component
  const MENU_ITEMS = MENU_ITEM_KEYS.map(i => ({ ...i, label: t(i.labelKey) }));
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null);
  const [perderOpen, setPerderOpen] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [selectedMotivo, setSelectedMotivo] = useState(null);
  const [perdendo, setPerdendo] = useState(false);
  const [ganhandoOpen, setGanhandoOpen] = useState(false);
  const [ganhando, setGanhando] = useState(false);
  const [ganhoDate, setGanhoDate] = useState("");
  const [ganhoEditing, setGanhoEditing] = useState(false);
  const [ganhoConfirmOpen, setGanhoConfirmOpen] = useState(false);
  const [contractRuleBlockOpen, setContractRuleBlockOpen] = useState(false);
  const [contractRuleBlockMsg, setContractRuleBlockMsg] = useState("");
  const [reabrirOpen, setReopenOpen] = useState(false);
  const [reabrindo, setReabrindo] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingMoveStageId, setPendingMoveStageId] = useState(null);
  const [pendingMoveValues, setPendingMoveValues] = useState({});
  // Tasks tab
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ titulo: "", tipo: "", data_vencimento: "", prioridade: "media", descricao: "" });
  const [editingTask, setEditingTask] = useState(null);
  const [taskDeleteTarget, setTaskDeleteTarget] = useState(null);
  const [reopenTaskTarget, setReopenTaskTarget] = useState(null);
  const [concludeTaskTarget, setConcludeTaskTarget] = useState(null);
  const [historyDetailEntry, setHistoryDetailEntry] = useState(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [emailList, setEmailList] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailDetailItem, setEmailDetailItem] = useState(null);
  const [smtpConfigured, setSmtpConfigured] = useState(null);
  const [whatsappConfigured, setWhatsappConfigured] = useState(null);
  const [phonePickerOpen, setPhonePickerOpen] = useState(false);
  const [phonePickerNumbers, setPhonePickerNumbers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filePreview, setFilePreview] = useState(null); // {url, filename, type}
  const [previewZoom, setPreviewZoom] = useState(100);
  const [contractData, setContractData] = useState(null);
  const [contractList, setContractList] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [billingWizardOpen, setBillingWizardOpen] = useState(false);
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [isentoConfirmOpen, setIsentoConfirmOpen] = useState(false);
  const [localIsento, setLocalIsento] = useState(null);
  const [contractWizardOpen, setContractWizardOpen] = useState(false);
  const [contractWizardData, setContractWizardData] = useState(null);
  const [contractStageRules, setContractStageRules] = useState({});
  const [attachContractOpen, setAttachContractOpen] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [attachName, setAttachName] = useState("");
  const [attachUploading, setAttachUploading] = useState(false);
  const [billingStageRules, setBillingStageRules] = useState({});
  const [billingList, setBillingList] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const isIsento = localIsento !== null ? localIsento : !!negocio?.cobranca_isenta;
  const visibleMenuItems = MENU_ITEMS.filter(i => {
    if (i.key === "contrato") return negocio?.estagio_id && contractStageRules[negocio.estagio_id];
    if (i.key === "cobranca") {
      // Show if current stage has billing rule OR if any stage in this pipeline has billing rules OR deal is marked isento
      if (negocio?.estagio_id && billingStageRules[negocio.estagio_id]) return true;
      if (Object.keys(billingStageRules).length > 0) return true;
      if (isIsento) return true;
      return false;
    }
    return true;
  });
  const [noteList, setNoteList] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeleteTarget, setNoteDeleteTarget] = useState(null);
  const [concludeResultado, setConcludeResultado] = useState("");
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  // AI Summary
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryAt, setAiSummaryAt] = useState("");
  const [aiSummaryBy, setAiSummaryBy] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiFetched, setAiFetched] = useState(false);
  const [aiStepDone, setAiStepDone] = useState(null);
  const [aiContextOpen, setAiContextOpen] = useState(false);
  const [aiContextText, setAiContextText] = useState("");
  const [aiContextLoading, setAiContextLoading] = useState(false);
  const [aiCooldownEnd, setAiCooldownEnd] = useState(null); // timestamp when cooldown ends
  const [aiCooldownPct, setAiCooldownPct] = useState(0); // 0-100 progress
  const [aiCooldownMinutes, setAiCooldownMinutes] = useState(0); // from profile config
  const [quickWaOpen, setQuickWaOpen] = useState(false);
  const [quickWaText, setQuickWaText] = useState("");
  const [quickWaSending, setQuickWaSending] = useState(false);
  // Edit entity dialogs (from Resumo header)
  const [editEmpresaOpen, setEditEmpresaOpen] = useState(false);
  const [editContatoItem, setEditContatoItem] = useState(null);
  const [closedDealsForAvg, setClosedDealsForAvg] = useState([]);
  // Deal type extra data
  const [produtoConfig, setProdutoConfig] = useState([]);  // array of lines
  const [empresaStats, setEmpresaStats] = useState(null);
  const [addProdutoOpen, setAddProdutoOpen] = useState(false);
  const [addProdutoForm, setAddProdutoForm] = useState({ produto_id: "", quantidade: 1, valor_unitario: 0 });
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#F97316";
  const tc50 = appearance.colors?.["50"] || "#FFF7ED";

  // Navigation between negocios
  const currentNegIdx = negocios.findIndex(n => n.id === negocio?.id);
  const canGoPrev = currentNegIdx > 0;
  const canGoNext = currentNegIdx >= 0 && currentNegIdx < negocios.length - 1;
  const goToNegocio = (idx) => { if (onNavigate && negocios[idx]) onNavigate(negocios[idx]); };

  const fetchHistory = useCallback(async () => {
    if (!negocio?.id) return;
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/negocios/${negocio.id}/activity`, { withCredentials: true });
      setHistory(res.data);
    } catch { setHistory([]); }
    finally { setLoadingHistory(false); }
  }, [negocio?.id]);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/whatsapp/status`, { withCredentials: true })
        .then(res => setWhatsappEnabled(res.data?.status === "connected"))
        .catch(() => setWhatsappEnabled(false));
      // Check AI integration status
      axios.get(`${API}/integrations/ai_summary/status`, { withCredentials: true })
        .then(res => setAiEnabled(!!res.data.enabled))
        .catch(() => setAiEnabled(false));
    }
  }, [open]);

  // Fetch existing AI summary when deal opens
  useEffect(() => {
    if (open && negocio?.id && !aiFetched) {
      setAiFetched(true);
      axios.get(`${API}/negocios/${negocio.id}/ai-summary`)
        .then(res => {
          setAiSummary(res.data.summary || null);
          setAiSummaryAt(res.data.generated_at || "");
          setAiSummaryBy(res.data.generated_by || "");
          setAiStepDone(res.data.step_done || null);
        })
        .catch(() => {});
    }
    if (!open) { setAiFetched(false); setAiSummary(null); setAiSummaryAt(""); setAiSummaryBy(""); setAiStepDone(null); setAiCooldownEnd(null); setAiCooldownPct(0); }
  }, [open, negocio?.id, aiFetched]);

  // Fetch AI cooldown config from user's profile
  useEffect(() => {
    if (open && authUser) {
      const role = authUser.role;
      const pid = authUser.profile_id;
      if ((role === "owner" || role === "admin") && !pid) {
        setAiCooldownMinutes(0); // admins/owners without profile: no cooldown
      } else if (pid) {
        axios.get(`${API}/profiles`, { withCredentials: true })
          .then(res => {
            const prof = (res.data || []).find(p => p.id === pid);
            setAiCooldownMinutes(prof?.ai_cooldown_minutes || 0);
          })
          .catch(() => setAiCooldownMinutes(0));
      } else {
        setAiCooldownMinutes(0);
      }
    }
  }, [open, authUser]);

  // Cooldown timer: compute end time based on ai_summary_at + cooldown minutes
  useEffect(() => {
    if (!aiSummaryAt || !aiCooldownMinutes) { setAiCooldownEnd(null); setAiCooldownPct(100); return; }
    const summaryTime = new Date(aiSummaryAt).getTime();
    const endTime = summaryTime + aiCooldownMinutes * 60 * 1000;
    if (Date.now() >= endTime) { setAiCooldownEnd(null); setAiCooldownPct(100); return; }
    setAiCooldownEnd(endTime);
  }, [aiSummaryAt, aiCooldownMinutes]);

  // Animate cooldown progress
  useEffect(() => {
    if (!aiCooldownEnd) return;
    const totalMs = aiCooldownMinutes * 60 * 1000;
    const tick = () => {
      const remaining = aiCooldownEnd - Date.now();
      if (remaining <= 0) { setAiCooldownEnd(null); setAiCooldownPct(100); return; }
      setAiCooldownPct(Math.round(((totalMs - remaining) / totalMs) * 100));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [aiCooldownEnd, aiCooldownMinutes]);

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (open && negocio) {
      // Only reset menu when modal first opens, not on negocio data refresh
      if (!prevOpenRef.current) {
        setActiveMenu(initialTab || "resumo");
      }
      prevOpenRef.current = true;
      setSaveSuccess(false);
      fetchHistory();
      setForm({
        titulo: negocio.titulo || "",
        responsavel_id: negocio.responsavel_id || "",
        empresa_id: negocio.empresa_id || "",
        contato_id: negocio.contato_id || "",
        produto_id: negocio.produto_id || "",
        estagio_id: negocio.estagio_id || "",
        valor_setup: negocio.valor_setup ?? 0,
        valor_mrr: negocio.valor_mrr ?? 0,
        tipo_negocio: negocio.tipo_negocio || "",
        custom_fields: negocio.custom_fields || {},
      });
      // Fetch all deals in pipeline for average comparison (no status filter)
      if (negocio.pipeline_id) {
        axios.get(`${API}/negocios?pipeline_id=${negocio.pipeline_id}&include_finalized=true`,
          { withCredentials: true })
          .then(r => setClosedDealsForAvg((r.data || []).filter(n => n.id !== negocio.id)))
          .catch(() => setClosedDealsForAvg([]));
      }
      // Fetch type-specific data
      const tipo = negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";
      if (tipo === "recorrente_produto") {
        fetchProdutoConfig();
      } else if (tipo === "pontual" && negocio.empresa_id) {
        axios.get(`${API}/negocios/${negocio.id}/empresa-stats`, { withCredentials: true })
          .then(r => setEmpresaStats(r.data)).catch(() => setEmpresaStats(null));
      }
    }
  }, [open, negocio, fetchHistory]); // eslint-disable-line

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const fetchTasks = useCallback(async () => {
    if (!negocio?.id) return;
    setTasksLoading(true);
    try {
      const [tasksRes, typesRes] = await Promise.all([
        axios.get(`${API}/tasks/negocio/${negocio.id}`, { withCredentials: true }),
        axios.get(`${API}/task-types`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setTaskTypes(typesRes.data);
    } catch { setTasks([]); }
    finally { setTasksLoading(false); }
  }, [negocio?.id]);

  // Inline valor save (Resumo cards)
  const handleGenerateAiSummary = async () => {
    if (!negocio?.id) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/negocios/${negocio.id}/ai-summary`, {}, { withCredentials: true });
      setAiSummary(res.data.summary || null);
      setAiSummaryAt(res.data.generated_at || "");
      setAiSummaryBy(res.data.generated_by || "");
      setAiStepDone(null);
      toast.success("Resumo gerado com sucesso!");
      if (onSaved) onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao gerar resumo com IA";
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleViewAiContext = async () => {
    if (!negocio?.id) return;
    setAiContextLoading(true);
    setAiContextOpen(true);
    try {
      const res = await axios.get(`${API}/negocios/${negocio.id}/ai-context`, { withCredentials: true });
      setAiContextText(res.data.context || "");
    } catch {
      setAiContextText("Erro ao carregar contexto.");
    } finally {
      setAiContextLoading(false);
    }
  };

  const handleQuickWaSend = async () => {
    if (!quickWaText.trim() || quickWaSending) return;
    const contact = contatos?.find(c => c.id === negocio?.contato_id);
    const PHONE_TYPES = ["Celular", "WhatsApp", "Telefone", "Telefonico"];
    const phones = (contact?.tipos_contato || [])
      .filter(tc => PHONE_TYPES.includes(tc.tipo) && tc.valor)
      .map(tc => { const ddi = tc.ddi || "55"; return ddi + tc.valor.replace(/\D/g, ""); });
    const phone = phones[0] || "";
    if (!phone) { toast.error("Contato sem telefone cadastrado."); return; }
    setQuickWaSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        negocio_id: negocio.id, phone, type: "text", text: quickWaText.trim()
      }, { withCredentials: true });
      toast.success("WhatsApp enviado!");
      setQuickWaText("");
      setQuickWaOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar WhatsApp");
    } finally { setQuickWaSending(false); }
  };

  const handleAiNextStep = async (step) => {
    if (!step?.acao) return;
    const acao = step.acao;
    const acaoLabels = {
      enviar_whatsapp: "Enviar WhatsApp",
      cadastrar_tarefa: "Cadastrar Tarefa",
      cadastrar_anotacao: "Cadastrar Anotação",
      avancar: "Avançar Etapa",
      retroceder: "Retroceder Etapa",
    };

    if (acao === "enviar_whatsapp") {
      setQuickWaText(step.msg || "");
      setQuickWaOpen(true);
    } else if (acao === "cadastrar_tarefa") {
      setEditingTask(null);
      const parseDate = (d, h) => {
        if (!d) return "";
        const parts = d.split("/");
        if (parts.length !== 3) return "";
        return `${parts[2]}-${parts[1]}-${parts[0]}T${h || "09:00"}`;
      };
      const prioMap = { "baixa": "baixa", "média": "media", "media": "media", "alta": "alta", "urgente": "urgente" };
      // Match AI tipo to actual task types (case-insensitive)
      const aiTipo = (step.tipo || "").trim();
      const matchedType = taskTypes.find(tt => tt.name.toLowerCase() === aiTipo.toLowerCase());
      setTaskForm({
        titulo: step.titulo || "",
        tipo: matchedType?.name || aiTipo,
        prioridade: prioMap[(step.prioridade || "media").toLowerCase()] || "media",
        data_vencimento: parseDate(step.data_vencimento, step.hora_vencimento),
        descricao: "",
      });
      setTaskDialogOpen(true);
    } else if (acao === "cadastrar_anotacao") {
      setEditingNote({ _prefill: true, corpo_html: `<p>${step.titulo || ""}</p>` });
      setNoteEditorOpen(true);
    } else if (acao === "avancar" && currentStageIdx < stages.length - 1) {
      moveStage(stages[currentStageIdx + 1]?.id);
    } else if (acao === "retroceder" && currentStageIdx > 0) {
      moveStage(stages[currentStageIdx - 1]?.id);
    }

    // Mark step as done
    const doneData = {
      acao: acaoLabels[acao] || acao,
      executed_at: new Date().toISOString(),
      executed_by: authUser?.name || "",
    };
    setAiStepDone(doneData);
    try {
      await axios.patch(`${API}/negocios/${negocio.id}/ai-step-done`, doneData, { withCredentials: true });
    } catch {}
  };

  const handleInlineSaveValor = async (field, val) => {
    try {
      const payload = { [field]: val };
      await axios.patch(`${API}/negocios/${negocio.id}/valores`, payload, { withCredentials: true });
      if (onSaved) onSaved();
    } catch (e) { console.error(e); }
  };

  // Email list
  const fetchEmails = useCallback(async () => {
    if (!negocio?.id) return;
    setEmailsLoading(true);
    try {
      const res = await axios.get(`${API}/emails?negocio_id=${negocio.id}`, { withCredentials: true });
      setEmailList(res.data);
    } catch { setEmailList([]); }
    finally { setEmailsLoading(false); }
  }, [negocio?.id]);

  // Notes list
  const fetchNotes = useCallback(async () => {
    if (!negocio?.id) return;
    setNotesLoading(true);
    try {
      const res = await axios.get(`${API}/notes?negocio_id=${negocio.id}`, { withCredentials: true });
      setNoteList(res.data);
    } catch { setNoteList([]); }
    finally { setNotesLoading(false); }
  }, [negocio?.id]);

  const fetchFiles = useCallback(async () => {
    if (!negocio?.id) return;
    setFilesLoading(true);
    try {
      const res = await axios.get(`${API}/files/list?negocio_id=${negocio.id}`);
      setFileList(res.data);
    } catch { setFileList([]); }
    finally { setFilesLoading(false); }
  }, [negocio?.id]);

  useEffect(() => { if (open && negocio?.id) fetchFiles(); }, [open, negocio?.id, fetchFiles]);
  useEffect(() => { setLocalIsento(null); }, [negocio?.id]);

  // Fetch contracts for this deal
  const fetchContracts = useCallback((syncFirst = false) => {
    if (!negocio?.id) return;
    setContractLoading(true);
    const doFetch = () => axios.get(`${API}/autentique/contracts?negocio_id=${negocio.id}`).then(r => {
      const list = r.data || [];
      setContractList(list);
      setContractData(list[0] || null);
    }).catch(() => { setContractList([]); setContractData(null); }).finally(() => setContractLoading(false));
    if (syncFirst) {
      // Show local data immediately, then sync and refresh
      doFetch().then(() => {
        axios.post(`${API}/autentique/sync-pending`, {}, { withCredentials: true })
          .then(() => {
            // Re-fetch after sync to get updated statuses
            axios.get(`${API}/autentique/contracts?negocio_id=${negocio.id}`).then(r => {
              setContractList(r.data || []);
              setContractData((r.data || [])[0] || null);
            }).catch(() => {});
          }).catch(() => {});
      });
    } else {
      doFetch();
    }
  }, [negocio?.id]);

  useEffect(() => { if (open && negocio?.id) fetchContracts(); }, [open, negocio?.id, fetchContracts]);

  // Fetch contract stage rules for the pipeline
  useEffect(() => {
    if (open && negocio?.pipeline_id) {
      axios.get(`${API}/autentique/pipeline-contract-rules?pipeline_id=${negocio.pipeline_id}`, { withCredentials: true })
        .then(r => setContractStageRules(r.data || {}))
        .catch(() => setContractStageRules({}));
    }
  }, [open, negocio?.pipeline_id]);

  // Fetch billing stage rules for the pipeline
  useEffect(() => {
    if (open && negocio?.pipeline_id) {
      axios.get(`${API}/asaas/pipeline-billing-rules?pipeline_id=${negocio.pipeline_id}`, { withCredentials: true })
        .then(r => setBillingStageRules(r.data || {}))
        .catch(() => setBillingStageRules({}));
    }
  }, [open, negocio?.pipeline_id]);

  // Fetch billings for this deal
  const fetchBillings = useCallback((syncFirst = false) => {
    if (!negocio?.id) return;
    setBillingLoading(true);
    const doFetch = () => axios.get(`${API}/asaas/billings?negocio_id=${negocio.id}`).then(r => {
      setBillingList(r.data || []);
    }).catch(() => setBillingList([])).finally(() => setBillingLoading(false));
    if (syncFirst) {
      doFetch().then(() => {
        axios.post(`${API}/asaas/sync-pending`, {}, { withCredentials: true }).then(() => {
          axios.get(`${API}/asaas/billings?negocio_id=${negocio.id}`).then(r => setBillingList(r.data || [])).catch(() => {});
        }).catch(() => {});
      });
    } else {
      doFetch();
    }
  }, [negocio?.id]);

  useEffect(() => { if (open && negocio?.id) fetchBillings(); }, [open, negocio?.id, fetchBillings]);

  // When stage changes, if on "contrato" or "cobranca" tab but new stage has no rule, switch to "resumo"
  useEffect(() => {
    if (negocio?.estagio_id) {
      if (activeMenu === "contrato" && !contractStageRules[negocio.estagio_id]) setActiveMenu("resumo");
      if (activeMenu === "cobranca" && !billingStageRules[negocio.estagio_id]) setActiveMenu("resumo");
    }
  }, [negocio?.estagio_id]); // eslint-disable-line

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("negocio_id", negocio?.id || "");
      try {
        await axios.post(`${API}/files/upload`, fd);
      } catch { toast.error(`Erro ao enviar ${file.name}`); }
    }
    setUploading(false);
    toast.success("Arquivo(s) enviado(s)!");
    fetchFiles();
    e.target.value = "";
  };

  // Product lines (Tipo 1)
  const fetchProdutoConfig = useCallback(async () => {
    if (!negocio?.id) return;
    try {
      const r = await axios.get(`${API}/negocio-produto-config/${negocio.id}`, { withCredentials: true });
      setProdutoConfig(Array.isArray(r.data) ? r.data : [r.data].filter(Boolean));
    } catch { setProdutoConfig([]); }
  }, [negocio?.id]);

  const handleAddProduto = async () => {
    try {
      const prod = produtos?.find(p => p.id === addProdutoForm.produto_id);
      await axios.post(`${API}/negocio-produto-config/${negocio.id}`, {
        produto_id: addProdutoForm.produto_id,
        produto_nome: prod?.nome || "",
        quantidade: parseFloat(addProdutoForm.quantidade) || 1,
        valor_unitario: parseFloat(addProdutoForm.valor_unitario) || 0,
      }, { withCredentials: true });
      setAddProdutoOpen(false);
      setAddProdutoForm({ produto_id: "", quantidade: 1, valor_unitario: 0 });
      fetchProdutoConfig();
      if (onSaved) onSaved();
    } catch (e) { console.error(e); }
  };

  const handleUpdateProdutoLine = async (lineId, field, val) => {
    const line = produtoConfig.find(l => l.id === lineId);
    if (!line) return;
    const qty   = field === "quantidade"    ? parseFloat(val) : line.quantidade;
    const price = field === "valor_unitario" ? parseFloat(val) : line.valor_unitario;
    try {
      await axios.put(`${API}/negocio-produto-config/${negocio.id}/${lineId}`,
        { quantidade: qty, valor_unitario: price }, { withCredentials: true });
      fetchProdutoConfig();
      if (onSaved) onSaved();
    } catch (e) { console.error(e); }
  };

  const handleDeleteProdutoLine = async (lineId) => {
    try {
      await axios.delete(`${API}/negocio-produto-config/${negocio.id}/${lineId}`, { withCredentials: true });
      fetchProdutoConfig();
      if (onSaved) onSaved();
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (open && activeMenu === "tarefas") fetchTasks();
    if (open && activeMenu === "email") fetchEmails();
    if (open && activeMenu === "anotacoes") fetchNotes();
  }, [open, activeMenu, fetchTasks, fetchEmails, fetchNotes]);

  // Preload task types when modal opens (needed for AI step "Cadastrar Tarefa" from Resumo tab)
  useEffect(() => {
    if (open && taskTypes.length === 0) {
      axios.get(`${API}/task-types`).then(r => setTaskTypes(r.data)).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  // Check SMTP + WhatsApp status on mount
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/emails/smtp-status`, { withCredentials: true })
      .then(res => setSmtpConfigured(res.data?.configured ?? false))
      .catch(() => setSmtpConfigured(false));
    axios.get(`${API}/whatsapp/status`, { withCredentials: true })
      .then(res => setWhatsappConfigured(res.data?.status === "connected"))
      .catch(() => setWhatsappConfigured(false));
  }, [open]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.titulo.trim()) return;
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, {
          ...taskForm,
          data_vencimento: taskForm.data_vencimento ? new Date(taskForm.data_vencimento).toISOString() : "",
        }, { withCredentials: true });
        toast.success("Tarefa atualizada!");
      } else {
        await axios.post(`${API}/tasks`, {
          ...taskForm,
          negocio_id: negocio.id,
          negocio_titulo: negocio.titulo,
          data_vencimento: taskForm.data_vencimento ? new Date(taskForm.data_vencimento).toISOString() : "",
        }, { withCredentials: true });
        toast.success("Tarefa criada!");
      }
      setTaskDialogOpen(false);
      setEditingTask(null);
      setTaskForm({ titulo: "", tipo: taskTypes[0]?.name || "", data_vencimento: "", prioridade: "media", descricao: "" });
      fetchTasks();
      if (onSaved) onSaved();
    } catch (err) { toast.error("Erro ao salvar tarefa"); console.error(err); }
  };

  const handleDeleteTask = async () => {
    if (!taskDeleteTarget) return;
    try {
      await axios.delete(`${API}/tasks/${taskDeleteTarget.id}`, { withCredentials: true });
      toast.success("Tarefa excluída!");
      setTaskDeleteTarget(null);
      fetchTasks();
      if (onSaved) onSaved();
    } catch (err) { toast.error("Erro ao excluir tarefa"); console.error(err); }
  };

  const handleConcludeTask = async () => {
    if (!concludeTaskTarget) return;
    try {
      await axios.patch(`${API}/tasks/${concludeTaskTarget.id}/concluir`, { resultado: concludeResultado }, { withCredentials: true });
      setConcludeTaskTarget(null); setConcludeResultado(""); fetchTasks();
      if (onSaved) onSaved();
    } catch (err) { console.error(err); }
  };

  if (!negocio) return null;

  const moveStage = async (newStageId) => {
    if (!newStageId || !negocio) return;
    const newStageIdx = stages.findIndex(s => s.id === newStageId);
    if (pipeline?.proibido_voltar && newStageIdx < currentStageIdx) return;
    if (pipeline?.passagem_obrigatoria && Math.abs(newStageIdx - currentStageIdx) > 1) return;

    // Check contract rule only when ADVANCING (not when going back)
    const stageRule = contractStageRules[negocio.estagio_id];
    if (newStageIdx > currentStageIdx && stageRule && stageRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/autentique/check-contract-rule`, { negocio_id: negocio.id }, { withCredentials: true });
        if (!check.data.allowed) {
          toast.error(check.data.message || "Regra de contrato não atendida.");
          return;
        }
      } catch { /* allow if check fails */ }
    }

    // Check billing rule only when ADVANCING
    const billingRule = billingStageRules[negocio.estagio_id];
    if (newStageIdx > currentStageIdx && billingRule && billingRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/asaas/check-billing-rule`, { negocio_id: negocio.id }, { withCredentials: true });
        if (!check.data.allowed) {
          toast.error(check.data.message || "Regra de cobrança não atendida.");
          return;
        }
      } catch { /* allow if check fails */ }
    }

    const isMissing = (val) => val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0);

    // Validate CURRENT stage (must be fully filled before leaving)
    // AND DESTINATION stage (must be filled to enter)
    const currentRequired = stageFieldsMap[negocio.estagio_id] || [];
    const destRequired    = stageFieldsMap[newStageId] || [];
    const allRequired     = [...new Set([...currentRequired, ...destRequired])];

    const missingFields = allRequired.filter(fn => isMissing(negocio.custom_fields?.[fn]));

    if (missingFields.length > 0) {
      const initVals = {};
      missingFields.forEach(fn => { initVals[fn] = negocio.custom_fields?.[fn] || ""; });
      setPendingMoveValues(initVals);
      setPendingMoveStageId(newStageId);
      return;
    }
    doMoveStage(newStageId);
  };

  const doMoveStage = async (newStageId) => {
    try {
      await axios.patch(`${API}/negocios/${negocio.id}/stage`, { estagio_id: newStageId }, { withCredentials: true });
      if (onSaved) onSaved();
    } catch (err) { console.error(err); }
  };

  const savePendingMove = async () => {
    if (!pendingMoveStageId) return;
    // Validate ALL required fields: current stage (source) + destination
    const sourceRequired = stageFieldsMap[negocio.estagio_id] || [];
    const destRequired   = stageFieldsMap[pendingMoveStageId] || [];
    const allRequired    = [...new Set([...sourceRequired, ...destRequired])];
    const pendingFields  = allRequired
      .map(fn => customFields.find(c => c.field_name === fn))
      .filter(Boolean)
      .filter(cf => { const v = negocio.custom_fields?.[cf.field_name]; return !v && v !== 0; });
    for (const cf of pendingFields) {
      const val = pendingMoveValues[cf.field_name];
      if (val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0)) {
        toast.error(`Preencha o campo obrigatório: ${cf.field_label}`); return;
      }
    }
    const updatedCustomFields = { ...(negocio.custom_fields || {}), ...pendingMoveValues };
    try {
      await axios.put(`${API}/negocios/${negocio.id}`, {
        titulo: negocio.titulo, empresa_id: negocio.empresa_id, contato_id: negocio.contato_id,
        produto_id: negocio.produto_id, pipeline_id: negocio.pipeline_id,
        responsavel_id: negocio.responsavel_id,
        valor_mrr: negocio.valor_mrr, valor_setup: negocio.valor_setup,
        estagio_id: pendingMoveStageId,
        custom_fields: updatedCustomFields,
      }, { withCredentials: true });
      setPendingMoveStageId(null); setPendingMoveValues({});
      if (onSaved) onSaved();
    } catch (err) { toast.error("Erro ao salvar."); console.error(err); }
  };

  const handleGanhar = async () => {
    // Check contract rule for current stage before allowing win
    const stageRule = contractStageRules[negocio.estagio_id];
    if (stageRule && stageRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/autentique/check-contract-rule`, { negocio_id: negocio.id }, { withCredentials: true });
        if (!check.data.allowed) {
          setContractRuleBlockMsg(check.data.message || "Regra de contrato não atendida.");
          setContractRuleBlockOpen(true);
          setGanhandoOpen(false);
          return;
        }
      } catch { /* allow if check fails */ }
    }
    // Check billing rule
    const billingRule = billingStageRules[negocio.estagio_id];
    if (billingRule && billingRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/asaas/check-billing-rule`, { negocio_id: negocio.id }, { withCredentials: true });
        if (!check.data.allowed) {
          setContractRuleBlockMsg(check.data.message || "Regra de cobrança não atendida.");
          setContractRuleBlockOpen(true);
          setGanhandoOpen(false);
          return;
        }
      } catch { /* allow if check fails */ }
    }
    await _executeGanho();
  };

  const _executeGanho = async () => {
    setGanhando(true);
    try {
      const payload = {};
      if (ganhoDate) payload.ganho_em = new Date(ganhoDate + "T12:00:00").toISOString();
      await axios.patch(`${API}/negocios/${negocio.id}/ganhar`, payload, { withCredentials: true });
      toast.success(ganhoEditing ? "Data de ganho atualizada!" : "Negócio ganho!");
      setGanhandoOpen(false);
      setGanhoEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao confirmar ganho";
      toast.error(msg);
    }
    finally { setGanhando(false); }
  };

  const handleReabrir = async () => {
    setReabrindo(true);
    try {
      await axios.patch(`${API}/negocios/${negocio.id}/reabrir`, {}, { withCredentials: true });
      setReopenOpen(false);
      if (onSaved) onSaved(); // refreshes negocio prop without closing
    } catch (err) { console.error(err); }
    finally { setReabrindo(false); }
  };

  const openPerder = async () => {
    setSelectedMotivo(null);
    try {
      const res = await axios.get(`${API}/pipelines/${negocio.pipeline_id}/motivos-perda`, { withCredentials: true });
      setMotivos(res.data);
    } catch { setMotivos([]); }
    setPerderOpen(true);
  };

  const handlePerder = async () => {
    setPerdendo(true);
    try {
      const motivo = motivos.find(m => m.id === selectedMotivo);
      await axios.patch(`${API}/negocios/${negocio.id}/perder`, {
        motivo_perda_id: selectedMotivo || "",
        motivo_nome: motivo?.nome || "Não informado",
      }, { withCredentials: true });
      setPerderOpen(false);
      if (onSaved) onSaved(); // stays open, updates via prop
    } catch (err) { console.error(err); }
    finally { setPerdendo(false); }
  };

  const handleDuplicate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/negocios/${negocio.id}/duplicate`, {}, { withCredentials: true });
      setDuplicateOpen(false);
      if (onSaved) onSaved();
    } catch (err) { console.error(err); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/negocios/${negocio.id}`, { withCredentials: true });
      setDeleteOpen(false);
      if (onSaved) onSaved();
      onClose();
    } catch (err) { console.error(err); }
    finally { setActionLoading(false); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    // Validate all required custom fields (stages 0 to current)
    for (const fn of visibleCustomFieldNames) {
      const val = form.custom_fields?.[fn];
      if (val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0)) {
        const cf = customFields.find(c => c.field_name === fn);
        if (cf) { alert(`Campo obrigatório: ${cf.field_label}`); return; }
      }
    }
    setSaving(true);
    try {
      const tipo = form.tipo_negocio || "recorrente_setup";
      const mrr   = (form.valor_mrr   === "" || form.valor_mrr   === null) ? 0 : parseFloat(form.valor_mrr)   || 0;
      const setup = (form.valor_setup === "" || form.valor_setup === null) ? 0 : parseFloat(form.valor_setup) || 0;
      const payload = {
        ...form,
        pipeline_id: negocio.pipeline_id,
        responsavel_id: form.responsavel_id || negocio.responsavel_id || "",
        tipo_negocio: tipo,
        valor_mrr:     tipo === "recorrente_setup" ? mrr   : 0,
        valor_setup:   tipo === "recorrente_setup" ? setup : 0,
        valor_negocio: tipo === "recorrente_setup" ? mrr + setup : 0,
        valor_tcv:     tipo === "recorrente_setup" ? (mrr * 12) + setup : 0,
      };
      await axios.put(`${API}/negocios/${negocio.id}`, payload, { withCredentials: true });
      toast.success("Negócio salvo com sucesso!");
      if (onSaved) onSaved();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  // === Computed values ===
  const currentStage = stages.find(s => s.id === negocio.estagio_id);
  const currentStageIdx = stages.findIndex(s => s.id === negocio.estagio_id);
  const isFinalized = negocio.status === "ganho" || negocio.status === "perdido";
  const today = new Date();
  const finalizacaoDate = negocio.data_finalizacao ? new Date(negocio.data_finalizacao) : null;

  // If the deal is finalized (ganho/perdido), freeze the clock at data_finalizacao
  // so SLA days don't keep accumulating after the deal is closed
  const refDate = finalizacaoDate || today;

  // Use same source of truth as KanbanCard: document fields (not history)
  const daysInStage = Math.max(0, Math.floor(
    (refDate - new Date(negocio.estagio_updated_at || negocio.created_at)) / (1000 * 60 * 60 * 24)
  ));
  const daysActive = Math.max(0, Math.floor((today - new Date(negocio.created_at)) / (1000 * 60 * 60 * 24)));

  // Days since last activity: updated_at → estagio_updated_at → created_at
  const daysSinceActivity = Math.max(0, Math.floor(
    (refDate - new Date(negocio.updated_at || negocio.estagio_updated_at || negocio.created_at)) / (1000 * 60 * 60 * 24)
  ));

  // MRR/Setup field names from form layout (to avoid showing them twice)
  const allLayoutFields = [...(formLayout || []), ...((formSections || []).flatMap(s => s.fields || []))];
  const mrrSetupFieldNames = new Set(
    allLayoutFields.filter(f => f.mrr_field || f.setup_field).map(f => f.field_name)
  );

  // Full entity records — with fallback to name-only if ID not found in list
  const empresaRecord = empresas?.find(e => e.id === negocio.empresa_id);
  const contatoRecord = contatos?.find(c => c.id === negocio.contato_id);
  const produtoRecord = produtos?.find(p => p.id === negocio.produto_id);

  // Internal fields to hide when showing full entity data
  const SKIP_FIELDS = new Set(["id", "tenant_id", "deleted", "deleted_at", "_id", "created_at", "updated_at", "empresa_id", "contato_id"]);

  // Icon mapping for common field names in entities
  const ENTITY_FIELD_ICONS = {
    nome: Tag, sku: Hash, quantidade: Package, categoria: Folder,
    preco: DollarSign, preco_custo: DollarSign, preco_venda: DollarSign, preco_unitario: DollarSign,
    email: Mail, telefone: Phone, website: Globe, cnpj: Hash, cpf: Hash,
    endereco: MapPin, cidade: MapPin, estado: MapPin, cep: MapPin, pais: Globe,
    descricao: FileText, empresa_nome: Building2, empresa: Building2,
    tipos_contato: Tag, observacao: FileText, site: Globe, responsavel: UserCheck,
  };

  function getEntityFieldIcon(key) {
    const lower = key.toLowerCase().replace(/ /g, "_");
    return ENTITY_FIELD_ICONS[lower] || ChevronRight;
  }

  const TIPO_ICONS = { Telefonico: Phone, WhatsApp: MessageCircle, Email: Mail };

  function renderEntityFields(record) {
    if (!record) return null;
    const rows = [];
    const EXCLUDED = new Set([...SKIP_FIELDS, "custom_fields"]);
    for (const [k, v] of Object.entries(record)) {
      if (EXCLUDED.has(k) || v === null || v === undefined || v === "") continue;
      // tipos_contato — array of {tipo, valor}
      if (k === "tipos_contato" && Array.isArray(v) && v.length > 0) {
        v.forEach((tc, i) => {
          const TIcon = TIPO_ICONS[tc.tipo] || Phone;
          rows.push(
            <div key={`tc-${i}`} className="flex items-center justify-between ml-4">
              <span className="flex items-center gap-1 text-slate-400"><TIcon className="w-3 h-3 flex-shrink-0" />{tc.tipo}</span>
              <span className="font-medium text-slate-700 text-right">{tc.valor}</span>
            </div>
          );
        });
        continue;
      }
      // Custom fields object
      if (k === "custom_fields" || typeof v === "object") continue;
      let displayVal = String(v);
      // Format phone numbers
      if (["telefone", "celular", "phone", "fone", "whatsapp"].includes(k.toLowerCase())) {
        const digits = displayVal.replace(/\D/g, "");
        if (digits.length === 11) displayVal = `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
        else if (digits.length === 10) displayVal = `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
      }
      const label = k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      const Icon = getEntityFieldIcon(k);
      rows.push(
        <div key={k} className="flex items-center justify-between ml-4">
          <span className="flex items-center gap-1 text-slate-400">
            <Icon className="w-3 h-3 flex-shrink-0" />{label}
          </span>
          <span className="font-medium text-slate-700 text-right max-w-[55%] truncate">{displayVal}</span>
        </div>
      );
    }
    return rows;
  }

  // MRR/Setup: prefer system field, fallback to custom field configured as mrr/setup_field
  const mrrCF = allLayoutFields.find(f => f.mrr_field === true);
  const setupCF = allLayoutFields.find(f => f.setup_field === true);
  const getMrrForN = (n) => {
    if (n.valor_mrr > 0) return n.valor_mrr;
    if (mrrCF) { const v = parseBRNumber(n.custom_fields?.[mrrCF.field_name]); if (v > 0) return v; }
    const cf = n.custom_fields || {};
    return parseBRNumber(cf.mrr) || parseBRNumber(cf.MRR) || parseBRNumber(cf.valor_mrr) || 0;
  };
  const getSetupForN = (n) => {
    if (n.valor_setup > 0) return n.valor_setup;
    if (setupCF) { const v = parseBRNumber(n.custom_fields?.[setupCF.field_name]); if (v > 0) return v; }
    const cf = n.custom_fields || {};
    return parseBRNumber(cf.setup) || parseBRNumber(cf.Setup) || parseBRNumber(cf.valor_setup) || 0;
  };
  const getValorNegocioForN = (n) => {
    if (n.valor_negocio > 0) return n.valor_negocio;
    return getMrrForN(n) + getSetupForN(n);
  };
  const displayMrr   = getMrrForN(negocio);
  const displaySetup = getSetupForN(negocio);
  const total12 = (displayMrr * 12) + displaySetup;
  // Pipeline averages — use custom fields fallback for each deal
  const avg = (arr, fn) => { const vals = arr.map(fn).filter(v => v > 0); return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0; };
  // Compare only against WON deals of the SAME TYPE in the same pipeline
  const tipoAtualForAvg = negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";
  const closedDeals = closedDealsForAvg.filter(n =>
    (n.tipo_negocio || "recorrente_setup") === tipoAtualForAvg
  );
  const avgMrr   = avg(closedDeals, getMrrForN);
  const avgSetup = avg(closedDeals, getSetupForN);
  const avgArr   = avg(closedDeals, n => (getMrrForN(n) * 12) + getSetupForN(n));

  const customFieldsWithValues = customFields.filter(cf => {
    const v = negocio.custom_fields?.[cf.field_name];
    return v !== null && v !== undefined && v !== "";
  });
  // Activity timeline uses all events from the unified /activity endpoint
  const activityEvents = history; // already sorted by backend
  const filteredContatos = contatos;

  // Edit form layout
  const orderedLayout = formLayout?.length > 0
    ? [...formLayout].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [
        { field_name: "titulo", fixed: true, field_width: "100" },
        { field_name: "empresa", fixed: true, field_width: "100" },
        { field_name: "contato", fixed: true, field_width: "50" },
        { field_name: "produto", fixed: true, field_width: "50" },
        ...customFields.map(cf => ({ field_name: cf.field_name, fixed: false, field_width: "100" })),
      ];
  const sectionFieldNames = new Set();
  (formSections || []).forEach(sec => (sec.fields || []).forEach(f => sectionFieldNames.add(f.field_name)));

  // Compute which custom fields should be visible in the edit form:
  // Only fields required by stages 0 through currentStageIdx (progressive disclosure)
  const ALWAYS_FIXED = new Set(["titulo", "responsavel", "empresa", "contato", "produto", "valor_setup", "valor_mrr", "estagio"]);
  const visibleCustomFieldNames = new Set();
  for (let i = 0; i <= currentStageIdx; i++) {
    const stageId = stages[i]?.id;
    const requiredFields = stageFieldsMap[stageId] || [];
    requiredFields.forEach(fn => visibleCustomFieldNames.add(fn));
  }

  // Filter layout: always show fixed fields, show custom fields only if in visibleCustomFieldNames
  const filteredLayout = orderedLayout.filter(f => {
    if (sectionFieldNames.has(f.field_name)) return false;
    if (f.fixed) return true;
    return visibleCustomFieldNames.has(f.field_name);
  });

  const fixedRenderers = {
    titulo:   () => {
      const TIPO_LABELS = { recorrente_setup: "Rec. com Setup", recorrente_produto: "Rec. por Produto", pontual: "Venda Pontual" };
      const tipoAtual = form.tipo_negocio || "recorrente_setup";
      return (
        <div key="titulo">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-slate-700">{t("negocios.titleField")}</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                  <BarChart2 className="w-3 h-3" />{TIPO_LABELS[tipoAtual]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {Object.entries(TIPO_LABELS).map(([val, label]) => (
                  <DropdownMenuItem key={val} onClick={() => setForm({...form, tipo_negocio: val})}
                    className={`text-xs ${tipoAtual === val ? "font-semibold text-blue-600" : ""}`}>
                    {tipoAtual === val && <Check className="w-3 h-3 mr-1.5 text-blue-500" />}
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Input value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required />
        </div>
      );
    },
    responsavel: () => (
      <div key="responsavel">
        <Label className="text-slate-700 mb-2 block">{t("ui.responsavel")}<span className="text-rose-500">*</span></Label>
        <SearchSelect items={responsaveis.map(r => ({ id: r.user_id, nome: r.name, email: r.email }))} value={form.responsavel_id} onChange={v => setForm({...form, responsavel_id: v})} placeholder="Selecionar responsável..." emptyText="Nenhum usuário disponível."
          renderItem={(r) => (<div><span className="text-sm font-medium text-slate-800">{r.nome}</span>{r.email && <p className="text-xs text-slate-400">{r.email}</p>}</div>)} />
      </div>
    ),
    empresa:  () => moduleEnabled.empresas ? (
      <div key="empresa">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700">Empresa <span className="text-rose-500">*</span></Label>
          <button type="button" onClick={() => setQuickCreate("empresa")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
        </div>
        <SearchSelect items={empresas} value={form.empresa_id} onChange={v => setForm({...form, empresa_id: v})} placeholder="Buscar empresa..." emptyText="Nenhuma empresa."
          renderItem={(e) => (<div><span className="text-sm font-medium text-slate-800">{e.nome}</span></div>)} />
      </div>
    ) : null,
    contato:  () => moduleEnabled.contatos ? (
      <div key="contato">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700">Contato <span className="text-rose-500">*</span></Label>
          <button type="button" onClick={() => setQuickCreate("contato")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
        </div>
        <SearchSelect items={filteredContatos} value={form.contato_id} onChange={v => setForm({...form, contato_id: v})} placeholder="Buscar contato..." emptyText="Nenhum contato."
          renderItem={(c) => { const contacts = (c.tipos_contato||[]).slice(0,3).map(tc => tc.valor).join(" / "); return (<div><div className="flex items-center gap-1.5 min-w-0"><span className="text-sm font-medium text-slate-800 truncate">{c.nome}</span></div>{contacts && <p className="text-xs text-slate-400 truncate" title={contacts}>{contacts}</p>}</div>); }} />
      </div>
    ) : null,
    produto:  () => moduleEnabled.produtos ? (
      <div key="produto">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700">Produto <span className="text-rose-500">*</span></Label>
          <button type="button" onClick={() => setQuickCreate("produto")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
        </div>
        <SearchSelect items={produtos} value={form.produto_id} onChange={v => setForm({...form, produto_id: v})} placeholder="Buscar produto..." emptyText="Nenhum produto."
          renderItem={(p) => (<div><div className="flex items-center gap-1.5 flex-wrap"><span className="text-sm font-medium text-slate-800">{p.nome}</span>{p.sku && <span className="text-xs text-slate-400">SKU: {p.sku}</span>}</div>{p.preco_venda && <p className="text-xs text-slate-400">R$ {p.preco_venda}</p>}</div>)} />
      </div>
    ) : null,
    valor_setup: () => <div key="valor_setup"><Label className="text-slate-700">Valor Setup (R$)</Label><Input type="number" step="0.01" value={form.valor_setup} onChange={e => setForm({...form, valor_setup: e.target.value})} /></div>,
    valor_mrr:   () => <div key="valor_mrr"><Label className="text-slate-700">Valor MRR (R$)</Label><Input type="number" step="0.01" value={form.valor_mrr} onChange={e => setForm({...form, valor_mrr: e.target.value})} /></div>,
  };

  const renderLayoutItem = (item) => {
    // Always use fixedRenderer for system fields (item may lack fixed:true if placed in a section)
    if (fixedRenderers[item.field_name]) return fixedRenderers[item.field_name](item);
    const cf = customFields.find(c => c.field_name === item.field_name);
    if (cf) {
      const cfWithRequired = visibleCustomFieldNames.has(cf.field_name) ? { ...cf, required: true } : cf;
      return <CustomFieldsForm key={cf.field_name} fields={[cfWithRequired]} values={form.custom_fields} onChange={cfv => setForm({...form, custom_fields: cfv})} layout={[item]} />;
    }
    return null;
  };

  // Use col-span approach instead of pre-building pairs (avoids null rendering issues)
  // layoutRows kept as reference but rendering uses grid directly below

  // History content (reused in Resumo and Histórico)
  // ── Activity timeline configuration ──────────────────────────────────────
  const EVENT_CONFIG = {
    negocio_criado:     { icon: Plus,          color: "#10b981", bg: "#ecfdf5", label: "Negócio criado" },
    negocio_editado:    { icon: Pencil,         color: "#3b82f6", bg: "#eff6ff", label: "Negócio editado" },
    etapa_alterada:     { icon: ArrowRight,     color: tc500,     bg: `${tc500}15`, label: "Etapa alterada" },
    negocio_ganho:      { icon: Trophy,         color: "#10b981", bg: "#ecfdf5", label: "Negócio ganho" },
    negocio_perdido:    { icon: X,              color: "#ef4444", bg: "#fef2f2", label: "Negócio perdido" },
    tarefa_criada:      { icon: CheckSquare,    color: "#3b82f6", bg: "#eff6ff", label: "Tarefa criada" },
    tarefa_concluida:   { icon: CheckCircle2,   color: "#10b981", bg: "#ecfdf5", label: "Tarefa concluída" },
    tarefa_excluida:    { icon: Trash2,         color: "#94a3b8", bg: "#f8fafc", label: "Tarefa excluída" },
    tarefa_editada:     { icon: Pencil,         color: "#f59e0b", bg: "#fffbeb", label: "Tarefa editada" },
    tarefa_reaberta:    { icon: RotateCcw,      color: "#f59e0b", bg: "#fffbeb", label: "Tarefa reaberta" },
    anotacao_criada:    { icon: StickyNote,     color: "#8b5cf6", bg: "#f5f3ff", label: "Anotação criada" },
    anotacao_editada:   { icon: StickyNote,     color: "#8b5cf6", bg: "#f5f3ff", label: "Anotação editada" },
    anotacao_removida:  { icon: StickyNote,     color: "#94a3b8", bg: "#f8fafc", label: "Anotação removida" },
    email_enviado:      { icon: Mail,           color: "#06b6d4", bg: "#ecfeff", label: "E-mail enviado" },
    email_agendado:     { icon: Mail,           color: "#f59e0b", bg: "#fffbeb", label: "E-mail agendado" },
    email_erro:         { icon: Mail,           color: "#ef4444", bg: "#fef2f2", label: "Erro no e-mail" },
    produto_adicionado: { icon: Package,        color: "#10b981", bg: "#ecfdf5", label: "Produto adicionado" },
    produto_atualizado: { icon: Package,        color: "#3b82f6", bg: "#eff6ff", label: "Produto atualizado" },
    produto_removido:   { icon: Package,        color: "#94a3b8", bg: "#f8fafc", label: "Produto removido" },
    valores_atualizados:{ icon: DollarSign,     color: "#10b981", bg: "#ecfdf5", label: "Valores atualizados" },
    automacao:          { icon: Zap,            color: "#f59e0b", bg: "#fffbeb", label: "Automação" },
    whatsapp_recebido:  { icon: MessageCircle,  color: "#10b981", bg: "#ecfdf5", label: "WhatsApp recebido" },
    whatsapp_enviado:   { icon: Send,           color: tc500,     bg: `${tc500}15`, label: "WhatsApp enviado" },
    lead_formulario:    { icon: ClipboardList,  color: "#6366f1", bg: "#eef2ff", label: "Lead via formulário" },
    ligacao_efetuada:   { icon: PhoneOutgoing,  color: "#f97316", bg: "#fff7ed", label: "Ligação efetuada" },
    ligacao_recebida:   { icon: PhoneIncoming,  color: "#10b981", bg: "#ecfdf5", label: "Ligação recebida" },
    ligacao_transcrita: { icon: FileAudio,      color: "#8b5cf6", bg: "#f5f3ff", label: "Ligação transcrita" },
  };

  const HistoryContent = () => {
    if (loadingHistory) return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} />
      </div>
    );
    if (activityEvents.length === 0) return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
        <History className="w-8 h-8 mb-2 text-slate-300" />
        <p className="text-sm">{t("negocios.noHistory")}</p>
      </div>
    );

    const sorted = [...activityEvents].reverse();
    const groups = {};
    for (const entry of sorted) {
      const d = new Date(entry.created_at);
      const today = new Date();
      const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
      let dateLabel;
      if (d.toDateString() === today.toDateString()) dateLabel = "Hoje";
      else if (d.toDateString() === yesterday.toDateString()) dateLabel = "Ontem";
      else dateLabel = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
      if (!groups[dateLabel]) groups[dateLabel] = [];
      groups[dateLabel].push(entry);
    }

    return (
      <div className="relative">
        {Object.entries(groups).map(([dateLabel, entries], gi) => (
          <div key={dateLabel} className={gi > 0 ? "mt-5" : ""}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] font-bold tracking-wide px-2.5 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: tc500 }}>{dateLabel}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="relative ml-[5px]">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200" />
              {entries.map((entry, i) => {
                const cfg = EVENT_CONFIG[entry.type] || EVENT_CONFIG.negocio_editado;
                const Icon = cfg.icon;
                const hasDetails = entry.data_before || entry.data_after;
                const time = new Date(entry.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                const descText = entry.type === "etapa_alterada"
                  ? (() => {
                      const desc = entry.description?.replace(`Negocio '${negocio.titulo}' `, "") || entry.description || "";
                      return desc.replace(/^movido de '(.+)' para '(.+)'$/, (_, from, to) =>
                        `Movido de '${from}' para '${to}'`
                      ).replace(/^movido de/i, "Movido de");
                    })()
                  : entry.description;
                return (
                  <div key={entry.id || `e-${gi}-${i}`} className="relative flex items-start gap-2.5 pb-2 group pl-4">
                    <div className="absolute left-[-3px] top-[7px] w-[7px] h-[7px] rounded-full z-10" style={{ backgroundColor: cfg.color }} />
                    <div className="flex-1 min-w-0 py-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Icon className="w-3 h-3 flex-shrink-0" style={{ color: cfg.color }} />
                        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: cfg.color }}>{cfg.label}</span>
                        {entry.user_name && (
                          <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{entry.user_name}</span>
                        )}
                        {hasDetails && (
                          <button type="button" onClick={() => setHistoryDetailEntry(entry)}
                            className="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 rounded px-1 py-0.5 transition-colors" title="Ver detalhes">
                            <Eye className="w-2.5 h-2.5" />
                          </button>
                        )}
                        <span className="text-[10px] text-slate-300 ml-auto flex-shrink-0">{time}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{descText}</p>
                      {entry.sub && <p className="text-[10px] text-slate-400 truncate">{entry.sub}</p>}
                      {/* Call status icons with tooltips */}
                      {(entry.type === "ligacao_efetuada" || entry.type === "ligacao_recebida") && entry.data_after && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`w-5 h-5 rounded flex items-center justify-center ${entry.data_after.has_recording ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-300"}`}>
                                <Mic className="w-3 h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">{entry.data_after.has_recording ? "Gravação realizada" : "Sem gravação"}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`w-5 h-5 rounded flex items-center justify-center ${entry.data_after.has_transcription ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-300"}`}>
                                <FileText className="w-3 h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">{entry.data_after.has_transcription ? "Transcrição disponível" : "Sem transcrição"}</TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
    {/* Manual dark overlay (pointer-events-none so Dialer stays clickable) */}
    {open && <div className="fixed inset-0 z-[45] bg-black/80 pointer-events-none" />}
    <Dialog open={open} onOpenChange={(v) => { if (!v) return; }} modal={false}>
      <DialogContent className="!fixed !inset-0 !translate-x-0 !translate-y-0 !left-0 !top-0 w-screen h-screen max-w-none max-h-none !rounded-none !p-0 overflow-hidden flex flex-col gap-0 border-0 outline-none [&>button]:hidden" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>

        {/* Inner content wrapper with overflow control */}
        <div className="flex flex-col flex-1 overflow-hidden bg-white">
        {/* ===== HEADER ===== */}
        <div className="flex items-center gap-3 px-4 sm:px-6 py-4 flex-shrink-0" style={{ backgroundColor: tc500 }}>
          {/* Left: icon + title */}
          <div className="flex items-stretch gap-3 flex-1 min-w-0">
            <div className="flex items-center justify-center flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="text-base font-bold text-white font-heading leading-tight">{negocio.titulo}</h2>
                {currentStage && !negocio.status && (
                  <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold text-slate-800 bg-white flex-shrink-0">
                    <CircleDot className="w-3 h-3" />{currentStage.name}
                  </div>
                )}
              </div>
              {(negocio.empresa_nome || negocio.contato_nome || negocio.responsavel_nome) && (
                <div className="flex items-center gap-1.5 text-xs flex-wrap">
                  {/* Responsável */}
                  {negocio.responsavel_nome && (() => {
                    const cfg = stageCardLayout?.find?.(f => f.field_name === "responsavel");
                    const Icon = (cfg?.icon ? resolveIcon(cfg.icon) : null) || UserCheck;
                    return <span className="flex items-center gap-1 text-white/80"><Icon className="w-3 h-3" />{negocio.responsavel_nome}</span>;
                  })()}
                  {/* Empresa */}
                  {negocio.empresa_nome && (
                    <button type="button"
                      onClick={() => setEditEmpresaOpen(true)}
                      className="inline-flex items-center gap-1 border border-white/30 rounded px-1.5 py-0.5 hover:bg-white/15 text-white/90 transition-colors">
                      <Building2 className="w-3 h-3" />{negocio.empresa_nome}
                    </button>
                  )}
                  {/* Primary contato */}
                  {negocio.contato_nome && (
                    <button type="button"
                      onClick={() => setEditContatoItem(contatoRecord || { id: negocio.contato_id, nome: negocio.contato_nome })}
                      className="inline-flex items-center gap-1 border border-white/30 rounded px-1.5 py-0.5 hover:bg-white/15 text-white/90 transition-colors">
                      <User className="w-3 h-3" />{negocio.contato_nome}
                    </button>
                  )}
                  {/* Additional contacts */}
                  {(negocio.contato_nomes || []).map(c => (
                    <button key={c.id} type="button"
                      onClick={() => setEditContatoItem(contatos?.find(ct => ct.id === c.id) || { id: c.id, nome: c.nome })}
                      className="inline-flex items-center gap-1 border border-white/30 rounded px-1.5 py-0.5 hover:bg-white/15 text-white/90 transition-colors">
                      <User className="w-3 h-3" />{c.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: actions + close */}
          <div className="flex items-stretch gap-1.5 flex-shrink-0">
            {/* Status badge if deal is finalized */}
            {(negocio.status === "ganho" || negocio.status === "perdido") && (
              <button type="button"
                onClick={() => {
                  if (negocio.status === "ganho") {
                    const existing = negocio.ganho_em || negocio.data_finalizacao || "";
                    const dateStr = existing ? new Date(existing).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
                    setGanhoDate(dateStr);
                    setGanhoEditing(true);
                    setGanhandoOpen(true);
                  }
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-opacity ${negocio.status === "ganho" ? "bg-emerald-500/90 hover:opacity-80 cursor-pointer" : "bg-rose-500/90 cursor-default"}`}>
                <div className="flex items-center gap-1.5 py-px">
                  {negocio.status === "ganho" ? <Check className="w-4 h-4 text-white" /> : <ThumbsDown className="w-4 h-4 text-white" />}
                  <span className="text-sm font-bold text-white leading-none">{negocio.status === "ganho" ? t("negocios.wonStatus") : t("negocios.lostStatus")}</span>
                </div>
              </button>
            )}

            {/* Quick Action Buttons — header */}
            <div className="flex items-center gap-1">
              {/* Group 1: Communication + Content — always visible */}
              {whatsappEnabled && (
                <Tooltip delayDuration={300}><TooltipTrigger asChild>
                  <button onClick={() => setQuickWaOpen(true)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"><MessageCircle className="w-4 h-4" /></button>
                </TooltipTrigger><TooltipContent>WhatsApp</TooltipContent></Tooltip>
              )}
              {window.__goto_connected && (
                <Tooltip delayDuration={300}><TooltipTrigger asChild>
                  <button onClick={() => {
                    const contact = contatos?.find(c => c.id === negocio?.contato_id);
                    const nums = (contact?.tipos_contato || []).filter(tc => ["Celular","WhatsApp","Telefone"].includes(tc.tipo) && tc.valor).map(tc => ({ tipo: tc.tipo, valor: tc.valor.replace(/\D/g, "") }));
                    if (!nums.length) toast.error("Sem telefone.");
                    else if (nums.length === 1) window.dispatchEvent(new CustomEvent("pipe4:dial", { detail: { phone: nums[0].valor, negocio_id: negocio?.id } }));
                    else { setPhonePickerNumbers(nums); setPhonePickerOpen(true); }
                  }} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"><Phone className="w-4 h-4" /></button>
                </TooltipTrigger><TooltipContent>Ligar</TooltipContent></Tooltip>
              )}
              <Tooltip delayDuration={300}><TooltipTrigger asChild>
                <button onClick={() => { setEditingNote(null); setNoteEditorOpen(true); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"><StickyNote className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent>Nova Anotação</TooltipContent></Tooltip>
              <Tooltip delayDuration={300}><TooltipTrigger asChild>
                <button onClick={() => { setEditingTask(null); setTaskForm({ titulo: "", tipo: "", data_vencimento: "", prioridade: "media", descricao: "" }); setTaskDialogOpen(true); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"><CheckSquare className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent>Nova Tarefa</TooltipContent></Tooltip>

              {/* Group 2: Pipeline — only when not finalized */}
              {!isFinalized && (<>
                <div className="w-px h-5 bg-white/20 mx-0.5" />
                <Tooltip delayDuration={300}><TooltipTrigger asChild>
                  <button onClick={() => moveStage(stages[currentStageIdx - 1]?.id)} disabled={currentStageIdx <= 0 || pipeline?.proibido_voltar}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /></button>
                </TooltipTrigger><TooltipContent>{currentStageIdx > 0 ? `Voltar: ${stages[currentStageIdx - 1]?.name}` : "Primeira etapa"}</TooltipContent></Tooltip>
                <Tooltip delayDuration={300}><TooltipTrigger asChild>
                  <button onClick={() => moveStage(stages[currentStageIdx + 1]?.id)} disabled={currentStageIdx >= stages.length - 1}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
                </TooltipTrigger><TooltipContent>{currentStageIdx < stages.length - 1 ? `Avançar: ${stages[currentStageIdx + 1]?.name}` : "Última etapa"}</TooltipContent></Tooltip>
                <Tooltip delayDuration={300}><TooltipTrigger asChild>
                  <button onClick={openPerder} className="w-8 h-8 rounded-lg flex items-center justify-center bg-rose-500/80 hover:bg-rose-500 text-white transition-colors"><ThumbsDown className="w-4 h-4" /></button>
                </TooltipTrigger><TooltipContent>Perder</TooltipContent></Tooltip>
                {(!pipeline?.ganhar_ultima_etapa || currentStageIdx === stages.length - 1) && (
                  <Tooltip delayDuration={300}><TooltipTrigger asChild>
                    <button onClick={async () => {
                      // Check contract rule before opening ganho modal
                      const stageRule = contractStageRules[negocio.estagio_id];
                      if (stageRule && stageRule.rule !== "none") {
                        try {
                          const check = await axios.post(`${API}/autentique/check-contract-rule`, { negocio_id: negocio.id }, { withCredentials: true });
                          if (!check.data.allowed) {
                            setContractRuleBlockMsg(check.data.message || "Regra de contrato não atendida.");
                            setContractRuleBlockOpen(true);
                            return;
                          }
                        } catch { /* allow */ }
                      }
                      // Check billing rule
                      const billingRule = billingStageRules[negocio.estagio_id];
                      if (billingRule && billingRule.rule !== "none") {
                        try {
                          const check = await axios.post(`${API}/asaas/check-billing-rule`, { negocio_id: negocio.id }, { withCredentials: true });
                          if (!check.data.allowed) {
                            setContractRuleBlockMsg(check.data.message || "Regra de cobrança não atendida.");
                            setContractRuleBlockOpen(true);
                            return;
                          }
                        } catch { /* allow */ }
                      }
                      setGanhoDate(new Date().toISOString().slice(0, 10));
                      setGanhoEditing(false);
                      setGanhandoOpen(true);
                    }} className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/80 hover:bg-emerald-500 text-white transition-colors"><Trophy className="w-4 h-4" /></button>
                  </TooltipTrigger><TooltipContent>Ganhar</TooltipContent></Tooltip>
                )}
              </>)}
            </div>

            {/* Navigate between negocios */}
            <div className="flex items-center gap-0.5">
              <Tooltip delayDuration={300}><TooltipTrigger asChild>
                <button onClick={() => goToNegocio(currentNegIdx - 1)} disabled={!canGoPrev}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent>Negócio anterior</TooltipContent></Tooltip>
              <Tooltip delayDuration={300}><TooltipTrigger asChild>
                <button onClick={() => goToNegocio(currentNegIdx + 1)} disabled={!canGoNext}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent>Próximo negócio</TooltipContent></Tooltip>
            </div>

            {/* More menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => setViewAllOpen(true)} className="cursor-pointer text-xs gap-2">
                  <Eye className="w-3.5 h-3.5 text-slate-500" />Visualizar Dados
                </DropdownMenuItem>
                {(negocio.status === "ganho" || negocio.status === "perdido") && (
                  <DropdownMenuItem onClick={() => setReopenOpen(true)} className="cursor-pointer text-xs gap-2 text-amber-600">
                    <Layers className="w-3.5 h-3.5" />Reabrir negócio
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setDuplicateOpen(true)} className="cursor-pointer text-xs gap-2">
                  <Copy className="w-3.5 h-3.5 text-slate-500" />Duplicar negócio
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="cursor-pointer text-xs gap-2 text-rose-600">
                  <Trash2 className="w-3.5 h-3.5" />Excluir negócio
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ===== BODY ===== */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Sidebar — horizontal scrollable nav on mobile, vertical on desktop */}
          <div className="md:w-52 flex-shrink-0 border-b md:border-b-0 md:border-r border-slate-200 bg-slate-50 py-2 md:py-4 px-2 flex md:flex-col flex-row overflow-x-auto gap-0.5">
            <p className="hidden md:block text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">{t("negocios.menu")}</p>
            {visibleMenuItems.map(item => {
              const Icon = item.icon;
              const isActive = activeMenu === item.key;
              const isEmailDisabled = item.key === "email" && smtpConfigured === false;
              const isWhatsappDisabled = item.key === "whatsapp" && whatsappConfigured === false;
              const isIntegrationDisabled = isEmailDisabled || isWhatsappDisabled;
              const isAvailable = item.available && !isIntegrationDisabled;
              const disabledLabel = isIntegrationDisabled ? "APIs" : t("negocios.comingSoon");
              const disabledTooltip = isEmailDisabled ? t("emails.smtpNotConfigured") : isWhatsappDisabled ? "WhatsApp não configurado. Acesse Integrações." : "Disponível em breve";
              return (
                <Tooltip key={item.key}>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={() => { if (isAvailable) { setActiveMenu(item.key); if (item.key === "contrato") fetchContracts(true); if (item.key === "cobranca") fetchBillings(true); } }}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all whitespace-nowrap
                        ${isActive ? "text-white font-medium" : isAvailable ? "text-slate-600 hover:bg-slate-100" : "text-slate-400 cursor-default"}`}
                      style={isActive ? { backgroundColor: tc500 } : undefined}>
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : isAvailable ? "text-slate-400" : "text-slate-300"}`} strokeWidth={1.5} />
                      <span className="text-[12px] truncate flex-1 text-left">{item.label}</span>
                      {item.key === "whatsapp" && whatsappUnread > 0 && activeMenu !== "whatsapp" && (
                        <span className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5 leading-none flex-shrink-0 min-w-[18px] text-center"
                          style={{ backgroundColor: tc500 }}>
                          {whatsappUnread > 9 ? "9+" : whatsappUnread}
                        </span>
                      )}
                      {!isAvailable && <span className="text-[10px] bg-slate-200 text-slate-400 rounded px-1 py-0.5">{disabledLabel}</span>}
                    </button>
                  </TooltipTrigger>
                  {!isAvailable && <TooltipContent side="right">{disabledTooltip}</TooltipContent>}
                </Tooltip>
              );
            })}
          </div>

          {/* Content */}
          <div className={`flex-1 min-h-0 ${activeMenu === "whatsapp" ? "overflow-hidden flex flex-col" : "overflow-y-auto p-3 sm:p-5"}`}>

            {/* ===== RESUMO ===== */}
            {activeMenu === "resumo" && (
              <div className="space-y-5">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" style={{ color: tc500 }} />{t("negocios.resume")}
                  <span className="flex-1" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-slate-400 hover:text-slate-600 cursor-default transition-colors">
                        <Clock className="w-3.5 h-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <span className="text-xs">Última atualização: {new Date(negocio.updated_at || negocio.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </TooltipContent>
                  </Tooltip>
                </h3>
                {/* Compare Cards — type-aware */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(() => {
                    const tipo = negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";

                    if (tipo === "recorrente_produto") {
                      const total = produtoConfig.reduce((s, l) => s + (l.valor_total || 0), 0);
                      return (<>
                        <CompareCard label="Produtos" value={produtoConfig.length} avgValue={null} icon={Package} isCount />
                        <CompareCard label="Itens"    value={produtoConfig.reduce((s,l)=>s+(l.quantidade||0),0)} avgValue={null} icon={Package} isCount />
                        <CompareCard label="Valor Total" value={total} avgValue={null} icon={TrendingUp} isCalc />
                      </>);
                    }

                    if (tipo === "pontual") {
                      const valNeg       = negocio.valor_negocio ?? 0;
                      const totalAbertos = empresaStats?.total_abertos ?? 0;
                      const totalGanhos  = empresaStats?.total_ganhos  ?? 0;
                      const totalPerdidos= empresaStats?.total_perdidos ?? 0;
                      const ultimoIso    = empresaStats?.ultimo_negocio;
                      const ultimaData   = ultimoIso
                        ? new Date(ultimoIso + "T00:00:00").toLocaleDateString("pt-BR")
                        : null;
                      const haDias       = ultimoIso
                        ? `Há ${Math.floor((Date.now() - new Date(ultimoIso + "T00:00:00")) / 86400000)} dias`
                        : null;

                      // Avg valor_negocio of all deals in pipeline
                      const allClosed = closedDealsForAvg;
                      const getEffectiveValor = n =>
                        n.valor_negocio > 0 ? n.valor_negocio
                        : (getMrrForN(n) + getSetupForN(n));
                      const avgNeg = avg(allClosed, getEffectiveValor);
                      return (<>
                        <CompareCard label="Valor do Negócio"    value={valNeg}      avgValue={avgNeg > 0 ? avgNeg : null} icon={DollarSign} isEditable={!isFinalized} onSave={v => handleInlineSaveValor("valor_negocio", v)} themeColor={tc500} />
                        <EmpresaCard totalAbertos={totalAbertos} totalGanhos={totalGanhos} totalPerdidos={totalPerdidos} tc500={tc500} />
                        <UltimoNegocioCard data={ultimaData} haDias={haDias} />
                      </>);
                    }

                    // recorrente_setup (default)
                    return (<>
                      <CompareCard label="Setup" value={displaySetup} avgValue={avgSetup} icon={DollarSign} isEditable={!isFinalized} onSave={v => handleInlineSaveValor("valor_setup", v)} themeColor={tc500} />
                      <CompareCard label="MRR"   value={displayMrr}   avgValue={avgMrr}   icon={DollarSign} isEditable={!isFinalized} onSave={v => handleInlineSaveValor("valor_mrr", v)} themeColor={tc500} />
                      <CompareCard label="TCV"   value={total12}       avgValue={avgArr}   icon={TrendingUp} isCalc />
                    </>);
                  })()}
                </div>

                {/* Card Produtos — apenas Tipo 1 */}
                {(negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup") === "recorrente_produto" && (
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Produtos</span>
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{produtoConfig.length}</span>
                      </div>
                      {!isFinalized && (
                        <button type="button" onClick={() => setAddProdutoOpen(true)}
                          className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                          <Plus className="w-3 h-3" />Adicionar
                        </button>
                      )}
                    </div>

                    {/* Lines */}
                    {produtoConfig.length === 0 ? (
                      <div className="py-8 flex items-center justify-center gap-2 text-slate-400 text-xs">
                        <Package className="w-4 h-4 flex-shrink-0" />
                        Nenhum produto adicionado.
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-50">
                        {/* Table header */}
                        <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                          <span className="col-span-4">Produto</span>
                          <span className="col-span-2 text-center">Qtd</span>
                          <span className="col-span-3 text-right">Valor Unit.</span>
                          <span className="col-span-2 text-right">Total</span>
                          <span className="col-span-1"></span>
                        </div>
                        {produtoConfig.map(line => (
                          <ProdutoLine key={line.id} line={line}
                            onUpdate={isFinalized ? undefined : (field, val) => handleUpdateProdutoLine(line.id, field, val)}
                            onDelete={isFinalized ? undefined : () => handleDeleteProdutoLine(line.id)} />
                        ))}
                      </div>
                    )}

                    {/* Add produto MODAL */}
                    <Dialog open={addProdutoOpen} onOpenChange={setAddProdutoOpen}>
                      <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShoppingBag className="w-5 h-5" style={{ color: tc500 }} />Adicionar produto</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-slate-700 text-sm">Produto <span className="text-rose-500">*</span></Label>
                            <Select value={addProdutoForm.produto_id} onValueChange={v => {
                              const prod = produtos?.find(p => p.id === v);
                              setAddProdutoForm(f => ({
                                ...f, produto_id: v,
                                valor_unitario: prod?.valor || 0,
                              }));
                            }}>
                              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar produto..." /></SelectTrigger>
                              <SelectContent>
                                {(produtos || []).map(p => (
                                  <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-slate-700 text-sm">Quantidade</Label>
                              <Input type="number" min="0.01" step="0.01" value={addProdutoForm.quantidade}
                                onChange={e => setAddProdutoForm(f => ({...f, quantidade: e.target.value}))}
                                className="mt-1" />
                            </div>
                            <div>
                              <Label className="text-slate-700 text-sm">Valor unitário (R$)</Label>
                              <Input type="number" min="0" step="0.01" value={addProdutoForm.valor_unitario}
                                onChange={e => setAddProdutoForm(f => ({...f, valor_unitario: e.target.value}))}
                                className="mt-1" />
                            </div>
                          </div>
                          {addProdutoForm.produto_id && (
                            <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-xs text-slate-600 border border-slate-200">
                              Total: <strong>{fmt((parseFloat(addProdutoForm.quantidade)||1) * (parseFloat(addProdutoForm.valor_unitario)||0))}</strong>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            <Button type="button" variant="outline" className="flex-1" onClick={() => setAddProdutoOpen(false)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
                            <Button type="button" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} disabled={!addProdutoForm.produto_id} onClick={handleAddProduto}><Check className="w-4 h-4 mr-2" />Adicionar</Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {/* AI Summary Card */}
                {aiEnabled && (
                  <div data-testid="ai-summary-card" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4" style={{ color: tc500 }} />
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Resumo com IA</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {authUser?.role === "owner" && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              data-testid="ai-context-view-btn"
                              type="button"
                              onClick={handleViewAiContext}
                              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Ver dados enviados para a IA</TooltipContent>
                        </Tooltip>
                        )}
                        {(() => {
                          const isCooling = !!aiCooldownEnd && Date.now() < aiCooldownEnd;
                          const isDisabled = aiLoading || (isCooling && !isFinalized);
                          const remainSec = isCooling ? Math.max(0, Math.ceil((aiCooldownEnd - Date.now()) / 1000)) : 0;
                          const remainMin = Math.floor(remainSec / 60);
                          const remainSecMod = remainSec % 60;
                          const cooldownTip = isCooling
                            ? `Disponível em ${remainMin > 0 ? `${remainMin}m ` : ""}${remainSecMod}s. Intervalo configurado pelo administrador (${aiCooldownMinutes} min).`
                            : "";
                          // SVG circle progress params
                          const r = 8, circ = 2 * Math.PI * r;
                          const offset = circ - (aiCooldownPct / 100) * circ;
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  data-testid="ai-summary-generate-btn"
                                  type="button"
                                  onClick={handleGenerateAiSummary}
                                  disabled={isDisabled}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium border rounded px-2.5 py-1 transition-colors"
                                  style={{
                                    borderColor: isDisabled ? "#cbd5e1" : tc500,
                                    color: isDisabled ? "#94a3b8" : tc500,
                                    backgroundColor: isDisabled ? "#f8fafc" : `${tc500}08`,
                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                  }}
                                >
                                  {aiLoading ? (
                                    <><Loader2 className="w-3 h-3 animate-spin" />Gerando...</>
                                  ) : isCooling ? (
                                    <>
                                      <svg width="18" height="18" viewBox="0 0 20 20" className="flex-shrink-0">
                                        <circle cx="10" cy="10" r={r} fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
                                        <circle cx="10" cy="10" r={r} fill="none" stroke={tc500} strokeWidth="2.5"
                                          strokeDasharray={circ} strokeDashoffset={offset}
                                          strokeLinecap="round" transform="rotate(-90 10 10)"
                                          style={{ transition: "stroke-dashoffset 1s linear" }} />
                                        <text x="10" y="10" textAnchor="middle" dominantBaseline="central" fontSize="6" fontWeight="700" fill="#94a3b8">{aiCooldownPct}%</text>
                                      </svg>
                                      Aguarde...
                                    </>
                                  ) : aiSummary?.resumo ? (
                                    <><RotateCcw className="w-3 h-3" />Analisar com IA</>
                                  ) : (
                                    <><Zap className="w-3 h-3" />Analisar com IA</>
                                  )}
                                </button>
                              </TooltipTrigger>
                              {(isCooling || aiCooldownMinutes > 0) && !aiLoading && (
                                <TooltipContent>{isCooling ? cooldownTip : `Intervalo entre análises: ${aiCooldownMinutes} min`}</TooltipContent>
                              )}
                            </Tooltip>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      {aiLoading ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-slate-400 text-xs">
                          <Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} />
                          <span>Analisando dados do negócio com IA...</span>
                        </div>
                      ) : aiSummary?.resumo ? (
                        <div className="space-y-3">
                          {/* Classification badges */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {aiSummary.situacao && (() => {
                              const s = aiSummary.situacao.toLowerCase();
                              const cfg = s.includes("quente") ? { bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", icon: <TrendingUp className="w-3.5 h-3.5" />, tip: "Negócio quente: interações frequentes, interesse demonstrado, próximo do fechamento" }
                                : s.includes("morno") ? { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200", icon: <CircleDot className="w-3.5 h-3.5" />, tip: "Negócio morno: algumas interações mas sem compromisso claro" }
                                : { bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-200", icon: <TrendingDown className="w-3.5 h-3.5" />, tip: "Negócio frio: sem interações recentes ou lead desengajado" };
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-default ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                      {cfg.icon}{aiSummary.situacao}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-xs">{cfg.tip}</TooltipContent>
                                </Tooltip>
                              );
                            })()}
                            {aiSummary.qualidade_do_lead && (() => {
                              const q = aiSummary.qualidade_do_lead.toLowerCase();
                              const cfg = q.includes("alta") ? { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", icon: <BarChart2 className="w-3.5 h-3.5" />, tip: "Qualidade alta: dados completos, empresa relevante, engajamento alto" }
                                : q.includes("dia") ? { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200", icon: <BarChart2 className="w-3.5 h-3.5" />, tip: "Qualidade média: dados parciais, engajamento moderado" }
                                : { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", icon: <BarChart2 className="w-3.5 h-3.5" />, tip: "Qualidade baixa: poucos dados, sem engajamento" };
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-default ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                      {cfg.icon}{aiSummary.qualidade_do_lead}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-xs">{cfg.tip}</TooltipContent>
                                </Tooltip>
                              );
                            })()}
                            {aiSummary.pontos_de_atencao && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-600 border-amber-200 cursor-default">
                                    <AlertCircle className="w-3.5 h-3.5" />Atenção
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm text-xs">{aiSummary.pontos_de_atencao}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          {/* Resumo */}
                          <div className="prose prose-sm max-w-none text-slate-700 text-xs leading-relaxed [&_h1]:text-sm [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-1 [&_h2]:text-xs [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_p]:my-1 [&_strong]:text-slate-800"
                            dangerouslySetInnerHTML={{ __html: (() => {
                              let html = (aiSummary.resumo || "");
                              html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                              // Convert markdown lists to proper HTML
                              const lines = html.split('\n');
                              let result = [];
                              let inList = false;
                              for (const line of lines) {
                                const trimmed = line.trim();
                                if (trimmed.startsWith('- ') || trimmed.match(/^\d+\.\s/)) {
                                  if (!inList) { result.push('<ul>'); inList = true; }
                                  result.push(`<li>${trimmed.replace(/^-\s|^\d+\.\s/, '')}</li>`);
                                } else {
                                  if (inList) { result.push('</ul>'); inList = false; }
                                  if (trimmed) result.push(`<p>${trimmed}</p>`);
                                }
                              }
                              if (inList) result.push('</ul>');
                              return result.join('');
                            })() }}
                          />
                          {/* Próximo Passo Sugerido */}
                          {aiSummary.proximo_passo?.acao && !isFinalized && (() => {
                            if (aiStepDone) {
                              return (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-3">
                                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Passo sugerido executado</p>
                                    <p className="text-xs text-slate-600">
                                      <span className="font-semibold">{aiStepDone.acao}</span> realizado em {new Date(aiStepDone.executed_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} por <span className="font-medium">{aiStepDone.executed_by}</span>
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                            const step = aiSummary.proximo_passo;
                            const acaoMap = {
                              enviar_whatsapp: { icon: <MessageCircle className="w-4 h-4" />, label: "Enviar WhatsApp", desc: step.msg },
                              cadastrar_tarefa: { icon: <CheckSquare className="w-4 h-4" />, label: "Cadastrar Tarefa", desc: `${step.titulo || ""}${step.tipo ? ` (${step.tipo})` : ""}${step.data_vencimento ? ` — ${step.data_vencimento}` : ""}` },
                              cadastrar_anotacao: { icon: <StickyNote className="w-4 h-4" />, label: "Cadastrar Anotação", desc: step.titulo },
                              avancar: { icon: <ChevronRight className="w-4 h-4" />, label: "Avançar Etapa", desc: currentStageIdx < stages.length - 1 ? `Para: ${stages[currentStageIdx + 1]?.name}` : "" },
                              retroceder: { icon: <ChevronLeft className="w-4 h-4" />, label: "Retroceder Etapa", desc: currentStageIdx > 0 ? `Para: ${stages[currentStageIdx - 1]?.name}` : "" },
                            };
                            const cfg = acaoMap[step.acao];
                            if (!cfg) return null;
                            return (
                              <button
                                type="button"
                                data-testid="ai-next-step-btn"
                                onClick={() => handleAiNextStep(step)}
                                className="w-full rounded-lg border-2 px-4 py-3 flex items-center gap-3 text-left transition-all hover:shadow-md hover:scale-[1.01] hover:brightness-110 group"
                                style={{ borderColor: tc500, backgroundColor: `${tc500}0D` }}
                              >
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform"
                                  style={{ backgroundColor: tc500 }}>
                                  {cfg.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] font-bold uppercase tracking-wider opacity-70" style={{ color: tc500 }}>Próximo passo sugerido</p>
                                  <p className="text-sm font-semibold" style={{ color: tc500 }}>{cfg.label}</p>
                                  {cfg.desc && <p className="text-xs opacity-80 truncate" style={{ color: tc500 }}>{cfg.desc}</p>}
                                </div>
                                <ChevronRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" style={{ color: tc500 }} />
                              </button>
                            );
                          })()}
                          {aiSummaryAt && (
                            <div className="pt-2 border-t border-slate-100 flex items-center gap-1 text-[10px] text-slate-400">
                              <Clock className="w-3 h-3" />
                              Gerado em {new Date(aiSummaryAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}{aiSummaryBy && <> por {aiSummaryBy}</>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 gap-2 text-slate-400">
                          <Brain className="w-8 h-8 opacity-30" />
                          <p className="text-xs text-center">Clique em "Gerar Resumo" para que a IA analise todas as informações deste negócio.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Secondary metrics — SLA */}
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard label={t("sla.stageLimit")} value={daysInStage === 1 ? `1 ${t("common.day")}` : `${daysInStage} ${t("common.days")}`} sub={currentStage?.name || "—"} icon={Timer}
                    breach={!!(stageSla?.prazo_dias > 0 && daysInStage > stageSla.prazo_dias)} tc500={tc500} />
                  <MetricCard label={t("sla.inactivity")} value={daysSinceActivity === 1 ? `1 ${t("common.day")}` : `${daysSinceActivity} ${t("common.days")}`} sub={negocio.updated_at ? t("negocios.lastUpdate") : t("negocios.sinceCreation")} icon={Timer}
                    breach={!!(stageSla?.prazo_inatividade_dias > 0 && daysSinceActivity > stageSla.prazo_inatividade_dias)} tc500={tc500} />
                </div>

                {/* Informações completas do Negócio — 100% width */}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <p className="text-xs font-semibold text-slate-500 mb-4 uppercase tracking-wider">{t("negocios.dealInfo")}</p>
                  <div className="space-y-3 text-xs">

                    {/* ── NEGÓCIO ── */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Título</span>
                        <span className="font-semibold text-slate-800 text-right max-w-[60%] truncate">{negocio.titulo}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Pipeline / Etapa</span>
                        <span className="flex items-center gap-1.5">
                          {currentStage && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: currentStage.color || "#94a3b8" }} />}
                          <span className="font-medium text-slate-700">{pipeline?.name || "—"} / {currentStage?.name || "—"}</span>
                        </span>
                      </div>
                      {(() => {
                        const tipo = negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";
                        const TIPO_LABELS = { recorrente_setup: "Rec. com Setup", recorrente_produto: "Rec. por Produto", pontual: "Venda Pontual" };
                        return (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400">Tipo de Negócio</span>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{TIPO_LABELS[tipo] || tipo}</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── VALORES (de acordo com o tipo) ── */}
                    {(() => {
                      const tipo = negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";
                      if (tipo === "recorrente_setup") {
                        return (
                          <div className="pt-2 border-t border-slate-100 space-y-2">
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><DollarSign className="w-3 h-3" />Valores</p>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">MRR</span>
                              <span className="font-semibold text-slate-700">{fmt(displayMrr)}</span>
                            </div>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">Setup</span>
                              <span className="font-semibold text-slate-700">{fmt(displaySetup)}</span>
                            </div>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">TCV (MRR×12 + Setup)</span>
                              <span className="font-bold text-slate-800">{fmt(total12)}</span>
                            </div>
                          </div>
                        );
                      }
                      if (tipo === "recorrente_produto") {
                        const totalProd = produtoConfig.reduce((s, l) => s + (l.valor_total || 0), 0);
                        return (
                          <div className="pt-2 border-t border-slate-100 space-y-2">
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><DollarSign className="w-3 h-3" />Valores</p>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">Produtos vinculados</span>
                              <span className="font-medium text-slate-700">{produtoConfig.length}</span>
                            </div>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">Qtd. total itens</span>
                              <span className="font-medium text-slate-700">{produtoConfig.reduce((s, l) => s + (l.quantidade || 0), 0)}</span>
                            </div>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">Valor Total</span>
                              <span className="font-bold text-slate-800">{fmt(totalProd)}</span>
                            </div>
                          </div>
                        );
                      }
                      if (tipo === "pontual") {
                        return (
                          <div className="pt-2 border-t border-slate-100 space-y-2">
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><DollarSign className="w-3 h-3" />Valores</p>
                            <div className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">Valor do Negócio</span>
                              <span className="font-bold text-slate-800">{fmt(negocio.valor_negocio || 0)}</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* ── DATAS ── */}
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Calendar className="w-3 h-3" />Datas</p>
                      <div className="flex items-center justify-between ml-4">
                        <span className="text-slate-400">Criação</span>
                        <span className="font-medium text-slate-700">{fmtDate(negocio.created_at)}</span>
                      </div>
                      {negocio.custom_fields?.data_previsao && (
                        <div className="flex items-center justify-between ml-4">
                          <span className="text-slate-400">Previsão de fechamento</span>
                          <span className="font-medium text-slate-700">{fmtDateShort(negocio.custom_fields.data_previsao)}</span>
                        </div>
                      )}
                      {negocio.data_finalizacao && (
                        <div className="flex items-center justify-between ml-4">
                          <span className={negocio.status === "ganho" ? "flex items-center gap-1 text-emerald-500" : "flex items-center gap-1 text-rose-400"}>
                            {negocio.status === "ganho" ? <Check className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                            Finalização ({negocio.status === "ganho" ? "Ganho" : "Perdido"})
                          </span>
                          <span className={`font-semibold ${negocio.status === "ganho" ? "text-emerald-600" : "text-rose-600"}`}>{fmtDate(negocio.data_finalizacao)}</span>
                        </div>
                      )}
                      {negocio.motivo_perda && (
                        <div className="flex items-center justify-between ml-4">
                          <span className="text-rose-400">Motivo da perda</span>
                          <span className="font-medium text-rose-600">{negocio.motivo_perda}</span>
                        </div>
                      )}
                    </div>

                    {/* ── RESPONSÁVEL ── */}
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><UserCheck className="w-3 h-3" />{t("negocios.responsible")}</p>
                      <div className="flex items-center justify-between ml-4">
                        <span className="text-slate-400">Nome</span>
                        <span className="font-medium text-slate-700">{negocio.responsavel_nome || "—"}</span>
                      </div>
                    </div>

                    {/* ── EMPRESA ── */}
                    {moduleEnabled.empresas && (
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Building2 className="w-3 h-3" />{t("negocios.company")}</p>
                        {empresaRecord ? renderEntityFields(empresaRecord) : (
                          <div className="ml-4 text-slate-400 italic">Nenhuma empresa vinculada</div>
                        )}
                      </div>
                    )}

                    {/* ── CONTATO ── */}
                    {moduleEnabled.contatos && (
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><User className="w-3 h-3" />{t("negocios.contact")}</p>
                        {contatoRecord ? renderEntityFields(contatoRecord) : negocio.contato_nome ? (
                          <div className="flex items-center justify-between ml-4">
                            <span className="flex items-center gap-1 text-slate-400"><Tag className="w-3 h-3" />{t("common.name")}</span>
                            <span className="font-medium text-slate-700">{negocio.contato_nome}</span>
                          </div>
                        ) : (
                          <div className="ml-4 text-slate-400 italic">Nenhum contato vinculado</div>
                        )}
                      </div>
                    )}

                    {/* ── PRODUTO (apenas se NÃO for recorrente_produto, que já tem line items acima) ── */}
                    {moduleEnabled.produtos && (negocio.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup") !== "recorrente_produto" && (
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Package className="w-3 h-3" />{t("negocios.product")}</p>
                        {produtoRecord ? renderEntityFields(produtoRecord) : (
                          <div className="ml-4 text-slate-400 italic">Nenhum produto vinculado</div>
                        )}
                      </div>
                    )}

                    {/* ── CAMPOS PERSONALIZADOS ── */}
                    {customFieldsWithValues.filter(cf => !mrrSetupFieldNames.has(cf.field_name)).length > 0 && (
                      <div className="pt-2 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FileText className="w-3 h-3" />Campos Personalizados</p>
                        {customFieldsWithValues.filter(cf => !mrrSetupFieldNames.has(cf.field_name)).map(cf => {
                          let val = negocio.custom_fields?.[cf.field_name];
                          if (Array.isArray(val)) val = val.join(", ");
                          if (cf.field_type === "boolean") val = val ? "Sim" : "Não";
                          if (cf.field_type === "currency") val = fmt(parseBRNumber(val));
                          if (cf.field_type === "date") val = fmtDateShort(val);
                          if (cf.field_type === "datetime") val = fmtDate(val);
                          if (cf.field_type === "phone" && val) {
                            const digits = String(val).replace(/\D/g, "");
                            if (digits.length === 11) val = `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
                            else if (digits.length === 10) val = `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
                          }
                          return (
                            <div key={cf.id} className="flex items-center justify-between ml-4">
                              <span className="text-slate-400">{cf.field_label}</span>
                              <span className="font-medium text-slate-700 text-right max-w-[55%] truncate">{String(val)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── CAMPOS OBRIGATÓRIOS PENDENTES (etapa atual) ── */}
                    {(() => {
                      const currentReq = stageFieldsMap[negocio.estagio_id] || [];
                      const missing = currentReq.filter(fn => {
                        const v = negocio.custom_fields?.[fn];
                        return v === null || v === undefined || v === "";
                      });
                      if (missing.length === 0) return null;
                      const missingLabels = missing.map(fn => {
                        const cf = customFields.find(c => c.field_name === fn);
                        return cf?.field_label || fn;
                      });
                      return (
                        <div className="pt-2 border-t border-slate-100">
                          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[11px] font-semibold text-amber-700 mb-1">Campos obrigatórios pendentes nesta etapa:</p>
                              <div className="flex flex-wrap gap-1">
                                {missingLabels.map((label, i) => (
                                  <span key={i} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{label}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                </div>

              </div>
            )}

            {/* ===== EDITAR ===== */}
            {activeMenu === "editar" && (
              <div>
                <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Pencil className="w-4 h-4" style={{ color: tc500 }} />{t("negocios.editTitle")}
                </h3>
                {/* Form — pointer-events-none when finalized */}
                <div className={negocio.status ? "pointer-events-none opacity-60 select-none" : ""}>
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div className="space-y-3">
                    {/* CSS grid col-span: 50% = col-span-1, 100% = col-span-2 */}
                    <div className="grid grid-cols-2 gap-3">
                      {filteredLayout.map((item, i) => {
                        const rendered = renderLayoutItem(item);
                        if (!rendered) return null;
                        return <div key={i} className={item.field_width === "50" ? "col-span-1" : "col-span-2"}>{rendered}</div>;
                      })}
                    </div>
                    {(formSections || []).map((sec, si) => {
                      // Show section field if it's a known system field OR a visible custom field
                      const SYSTEM_FIELDS = new Set(["titulo", "responsavel", "empresa", "contato", "produto", "valor_setup", "valor_mrr", "estagio", "data_previsao"]);
                      const visibleSectionFields = (sec.fields || []).filter(sf =>
                        sf.fixed || SYSTEM_FIELDS.has(sf.field_name) || visibleCustomFieldNames.has(sf.field_name)
                      );
                      if (!visibleSectionFields.length) return null;
                      return (
                        <div key={`sec-${si}`}>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{sec.name}</p>
                          <div className="border border-slate-200 rounded-lg px-4 py-3">
                            <div className="grid grid-cols-2 gap-3">
                              {visibleSectionFields.map((sf, sfi) => {
                                const rendered = renderLayoutItem(sf);
                                if (!rendered) return null;
                                return <div key={sfi} className={sf.field_width === "50" ? "col-span-1" : "col-span-2"}>{rendered}</div>;
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!negocio.status && (
                    <div className="flex justify-end">
                      <Button type="submit" disabled={saving} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        {saving ? t("common.loading") : t("negocios.saveChanges")}
                      </Button>
                    </div>
                  )}
                </form>
                </div>
              </div>
            )}

            {/* ===== HISTÓRICO ===== */}
            {activeMenu === "historico" && (
              <div>
                <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <History className="w-4 h-4" style={{ color: tc500 }} />{t("negocios.historyTitle")}
                </h3>
                <HistoryContent />
              </div>
            )}

            {/* ===== TAREFAS ===== */}
            {activeMenu === "tarefas" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" style={{ color: tc500 }} />{t("tarefas.title")}
                  </h3>
                  {(
                    <button type="button" onClick={() => { setEditingTask(null); setTaskForm({ titulo: "", tipo: taskTypes[0]?.name || "", data_vencimento: "", prioridade: "media", descricao: "" }); setTaskDialogOpen(true); }}
                      className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                      <Plus className="w-3 h-3" />{t("tarefas.new")}
                    </button>
                  )}
                </div>
                {tasksLoading ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />)}</div>
                ) : tasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <CheckSquare className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">{t("negocios.noTasks")}</p>
                  </div>
                ) : tasks.map(task => {
                  const overdue = task.status === "aberta" && task.data_vencimento && new Date(task.data_vencimento) < new Date();
                  const openEditTask = () => {
                    const dv = task.data_vencimento ? new Date(task.data_vencimento).toISOString().slice(0, 16) : "";
                    setEditingTask(task);
                    setTaskForm({ titulo: task.titulo || "", tipo: task.tipo || "", data_vencimento: dv, prioridade: task.prioridade || "media", descricao: task.descricao || "" });
                    setTaskDialogOpen(true);
                  };
                  return (
                    <div key={task.id}
                      className={`rounded-lg border bg-white transition-all duration-200 group cursor-pointer ${overdue ? "border-rose-200" : task.status === "concluida" ? "border-emerald-500" : "border-slate-200"}`}
                      style={{ borderColor: undefined }}
                      onMouseEnter={e => { if (!overdue && task.status !== "concluida") e.currentTarget.style.borderColor = tc500; }}
                      onMouseLeave={e => e.currentTarget.style.borderColor = ''}
                      onClick={() => task.status === "concluida" ? setReopenTaskTarget(task) : openEditTask()}>
                      <div className="flex items-center gap-3 p-3">
                        <button onClick={(e) => {
                            e.stopPropagation();
                            if (task.status === "aberta") {
                              setConcludeTaskTarget(task);
                            } else if (task.status === "concluida") {
                              setReopenTaskTarget(task);
                            }
                          }}
                          className={`flex-shrink-0 ${task.status === "concluida" ? "text-emerald-500 hover:text-slate-400 cursor-pointer" : task.status === "aberta" ? "text-slate-300 hover:text-emerald-500 cursor-pointer" : "cursor-default"}`}>
                          {task.status === "concluida" ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                        </button>
                        <p className={`flex-1 text-sm font-medium ${task.status === "concluida" ? "line-through text-slate-400" : "text-slate-800"}`}>{task.titulo}</p>
                        {!isFinalized && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setTaskDeleteTarget(task); }}
                            className="text-slate-400 hover:text-rose-500 p-1 rounded hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 px-3 pb-2.5 pt-0 border-t border-slate-100 mx-3 mt-0 pt-2 text-[11px] text-slate-400">
                        {task.responsavel_nome && <span className="inline-flex items-center gap-1" title="Responsável"><UserCheck className="w-3 h-3" />{task.responsavel_nome}</span>}
                        {task.tipo && <span className="inline-flex items-center gap-1" title="Tipo da tarefa"><Tag className="w-3 h-3" />{task.tipo}</span>}
                        {task.prioridade && <span className={`inline-flex items-center gap-1 font-medium ${task.prioridade === "urgente" ? "text-rose-500" : task.prioridade === "alta" ? "text-amber-500" : ""}`} title="Prioridade"><AlertCircle className="w-3 h-3" />{task.prioridade === "baixa" ? "Baixa" : task.prioridade === "media" ? "Média" : task.prioridade === "alta" ? "Alta" : "Urgente"}</span>}
                        {task.data_vencimento && <span className={`inline-flex items-center gap-1 ${overdue ? "text-rose-500 font-medium" : ""}`} title="Data de vencimento"><Calendar className="w-3 h-3" />{new Date(task.data_vencimento).toLocaleDateString("pt-BR")}</span>}
                        {task.status === "concluida" && task.data_conclusao && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" title="Data de finalização"><CheckCircle2 className="w-3 h-3" />{new Date(task.data_conclusao).toLocaleDateString("pt-BR")}</span>}
                      </div>
                      {task.resultado && <p className="text-xs text-emerald-600 px-3 pb-2.5">{task.resultado}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ===== WHATSAPP ===== */}
            {activeMenu === "whatsapp" && (
              whatsappEnabled ? (
                <WhatsAppChat negocio={negocio} contact={contatoRecord} />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400 p-8">
                  <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-green-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 text-center">WhatsApp não habilitado</p>
                  <p className="text-xs text-center max-w-xs">
                    Ative a integração em <strong>Configurações → Integrações</strong> para habilitar o chat diretamente neste negócio.
                  </p>
                </div>
              )
            )}

            {/* ===== EMAIL ===== */}
            {activeMenu === "email" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Mail className="w-4 h-4" style={{ color: tc500 }} />{t("emails.title")}
                  </h3>
                  <button type="button" onClick={() => setEmailComposerOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                    <Plus className="w-3 h-3" />{t("emails.compose")}
                  </button>
                </div>
                {emailsLoading ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />)}</div>
                ) : emailList.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">{t("emails.noEmails")}</p>
                ) : (
                  <div className="space-y-2">
                    {emailList.map(em => {
                      const statusCfg = {
                        enviado: { color: "text-emerald-600", bg: "bg-emerald-50", icon: Check },
                        agendado: { color: "text-amber-600", bg: "bg-amber-50", icon: Clock },
                        erro: { color: "text-rose-600", bg: "bg-rose-50", icon: AlertCircle },
                      }[em.status] || { color: "text-slate-500", bg: "bg-slate-50", icon: Mail };
                      const StatusIcon = statusCfg.icon;
                      return (
                        <div key={em.id} className="rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition-colors cursor-pointer"
                          onClick={() => setEmailDetailItem(em)}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">{em.assunto}</p>
                              <p className="text-xs text-slate-400 truncate mt-0.5">{t("emails.to")}: {em.para.join(", ")}</p>
                            </div>
                            <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {t(`emails.status.${em.status}`)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                            <span>{fmtDate(em.enviado_em || em.agendado_para || em.created_at)}</span>
                            {em.created_by_name && <><span>·</span><span className="font-medium text-slate-500">{em.created_by_name}</span></>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ===== ANOTAÇÕES ===== */}
            {activeMenu === "anotacoes" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" style={{ color: tc500 }} />{t("notes.title")}
                  </h3>
                  <button type="button" onClick={() => { setEditingNote(null); setNoteEditorOpen(true); }}
                    className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                    <Plus className="w-3 h-3" />{t("notes.add")}
                  </button>
                </div>
                {notesLoading ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 bg-slate-50 rounded-lg animate-pulse" />)}</div>
                ) : noteList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <StickyNote className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">{t("notes.noNotes")}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {noteList.map(note => (
                      <div key={note.id}
                        className="rounded-lg border border-slate-200 bg-white p-3 cursor-pointer transition-all duration-200 group"
                        style={{ borderColor: undefined }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = tc500}
                        onMouseLeave={e => e.currentTarget.style.borderColor = ''}
                        onClick={() => { setEditingNote(note); setNoteEditorOpen(true); }}>
                        <div className="prose prose-sm max-w-none text-sm text-slate-700 line-clamp-4"
                          dangerouslySetInnerHTML={{ __html: note.corpo_html }} />
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-3 text-[11px] text-slate-400">
                            {note.created_by_name && <span className="inline-flex items-center gap-1" title="Autor"><UserCheck className="w-3 h-3" />{note.created_by_name}</span>}
                            <span className="inline-flex items-center gap-1" title="Data da anotação"><Clock className="w-3 h-3" />{fmtDate(note.updated_at || note.created_at)}</span>
                          </div>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setNoteDeleteTarget(note); }}
                            className="text-[11px] text-slate-400 hover:text-rose-500 px-1.5 py-0.5 rounded hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===== CONTRATO ===== */}
            {activeMenu === "contrato" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <FileSignature className="w-4 h-4" style={{ color: tc500 }} />Contratos
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <button className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
                      data-testid="attach-signed-btn"
                      onClick={() => { setAttachContractOpen(true); setAttachFile(null); setAttachName(""); }}>
                      <Paperclip className="w-3 h-3" />Anexar assinado
                    </button>
                    <button className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
                      data-testid="new-contract-btn"
                    onClick={() => {
                      const stageRule = contractStageRules[negocio.estagio_id];
                      const templates = stageRule?.templates || [];
                      if (templates.length > 1) {
                        setContractWizardData({
                          negocio_id: negocio.id,
                          template_id: "",
                          stageTemplates: templates,
                        });
                      } else {
                        setContractWizardData({
                          negocio_id: negocio.id,
                          template_id: templates[0]?.template_id || "",
                        });
                      }
                      setContractWizardOpen(true);
                    }}>
                    <Plus className="w-3 h-3" />Novo contrato
                  </button>
                  </div>
                </div>

                {contractLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
                ) : contractList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <FileSignature className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">Nenhum contrato enviado para este negócio.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contractList.map(c => {
                      const statusMap = { signed: { label: "Assinado", bg: "bg-emerald-100 text-emerald-700" }, rejected: { label: "Recusado", bg: "bg-rose-100 text-rose-700" }, revoked: { label: "Revogado", bg: "bg-slate-200 text-slate-600" }, pending: { label: "Pendente", bg: "bg-amber-100 text-amber-700" } };
                      const st = statusMap[c.status] || statusMap.pending;
                      const isManual = c.source === "manual_upload";
                      const sigCount = (c.signatures || []).length;
                      const signedCount = (c.signatures || []).filter(s => s.signed?.created_at).length;
                      return (
                        <button key={c.id} onClick={() => setSelectedContract(c)}
                          className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50/50 transition-colors text-left">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.status === "signed" ? "bg-emerald-100" : c.status === "revoked" ? "bg-slate-100" : c.status === "rejected" ? "bg-rose-100" : "bg-amber-100"}`}>
                            {isManual ? <Paperclip className="w-5 h-5 text-emerald-600" /> : <FileSignature className={`w-5 h-5 ${c.status === "signed" ? "text-emerald-600" : c.status === "revoked" ? "text-slate-400" : c.status === "rejected" ? "text-rose-500" : "text-amber-600"}`} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.doc_name}</p>
                            <p className="text-[10px] text-slate-400">
                              {new Date(c.created_at).toLocaleDateString("pt-BR")}
                              {isManual ? " · Anexado manualmente" : ` · ${signedCount}/${sigCount} assinado(s)`}
                              {" · "}{c.created_by_name}
                            </p>
                          </div>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${st.bg}`}>{st.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Contract Detail Modal — shared component */}
                <ContractDetailModal
                  contract={selectedContract}
                  open={!!selectedContract}
                  onClose={() => setSelectedContract(null)}
                  onRefresh={fetchContracts}
                />
              </div>
            )}

            {/* ===== COBRANÇAS ===== */}
            {activeMenu === "cobranca" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Receipt className="w-4 h-4" style={{ color: tc500 }} />Cobranças
                    {isIsento && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Isento</span>}
                  </h3>
                  <div className="flex items-center gap-2">
                    {can("negocios", "liberar_isento") && (
                      <button className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-1.5 py-0.5 transition-colors cursor-pointer ${isIsento ? "border-emerald-400 text-emerald-600 bg-emerald-50" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                        onClick={() => setIsentoConfirmOpen(true)}>
                        {isIsento ? <><Check className="w-3 h-3" />Isento</> : <><AlertCircle className="w-3 h-3" />Isentar</>}
                      </button>
                    )}
                    {!isIsento && (
                      <>
                        <button className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
                          data-testid="manual-payment-btn"
                          onClick={() => setManualPaymentOpen(true)}>
                          <Check className="w-3 h-3" />Registrar pagamento
                        </button>
                        <button className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
                          data-testid="new-billing-btn"
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/asaas/negocio-billing-data/${negocio.id}`, { withCredentials: true });
                        if (r.data?.has_contracts === false) {
                          toast.error("Este negócio não possui contrato emitido ou assinado. Emita um contrato antes de gerar uma cobrança.");
                          return;
                        }
                      } catch {}
                      setBillingWizardOpen(true);
                    }}>
                    <Plus className="w-3 h-3" />Nova cobrança
                  </button>
                      </>
                    )}
                  </div>
                </div>

                {billingLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
                ) : billingList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <Receipt className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">Nenhuma cobrança emitida para este negócio.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {billingList.map(b => {
                      const statusMap = { PENDING: { label: "Pendente", bg: "bg-amber-100 text-amber-700" }, RECEIVED: { label: "Pago", bg: "bg-emerald-100 text-emerald-700" }, CONFIRMED: { label: "Confirmado", bg: "bg-emerald-100 text-emerald-700" }, RECEIVED_IN_CASH: { label: "Pago", bg: "bg-emerald-100 text-emerald-700" }, OVERDUE: { label: "Vencido", bg: "bg-rose-100 text-rose-700" }, CANCELLED: { label: "Cancelado", bg: "bg-slate-200 text-slate-600" } };
                      const st = statusMap[b.status] || statusMap.PENDING;
                      const typeLabels = { BOLETO: "Boleto", PIX: "Pix", CREDIT_CARD: "Cartão" };
                      const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
                      return (
                        <div key={b.id} onClick={() => setSelectedBilling(b)} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors cursor-pointer">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${b.status === "RECEIVED" || b.status === "CONFIRMED" ? "bg-emerald-100" : b.status === "OVERDUE" ? "bg-rose-100" : "bg-amber-100"}`}>
                            <Receipt className={`w-5 h-5 ${b.status === "RECEIVED" || b.status === "CONFIRMED" ? "text-emerald-600" : b.status === "OVERDUE" ? "text-rose-500" : "text-amber-600"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">{b.description || typeLabels[b.billing_type] || "Cobrança"}</p>
                            <p className="text-[10px] text-slate-400">{typeLabels[b.billing_type]} · {b.due_date ? new Date(b.due_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"} · {b.created_by_name}</p>
                          </div>
                          <span className="text-sm font-bold text-slate-800">{fmt(b.value)}</span>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${st.bg}`}>{st.label}</span>
                          {b.invoice_url && <a href={b.invoice_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-500"><ExternalLink className="w-3.5 h-3.5" /></a>}
                        </div>
                      );
                    })}
                  </div>
                )}

                <BillingDetailModal
                  billing={selectedBilling}
                  open={!!selectedBilling}
                  onClose={() => setSelectedBilling(null)}
                  onRefresh={fetchBillings}
                />
                <BillingWizardModal
                  open={billingWizardOpen}
                  onClose={() => setBillingWizardOpen(false)}
                  prefillNegocioId={negocio?.id}
                  onBillingSent={() => { fetchBillings(); setBillingWizardOpen(false); }}
                />
                <ManualPaymentModal
                  open={manualPaymentOpen}
                  onClose={() => setManualPaymentOpen(false)}
                  negocioId={negocio?.id}
                  onSaved={() => { fetchBillings(); setManualPaymentOpen(false); }}
                />
              </div>
            )}

            {/* ===== ARQUIVOS ===== */}
            {activeMenu === "arquivos" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Folder className="w-4 h-4" style={{ color: tc500 }} />Arquivos
                  </h3>
                  <label className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    {uploading ? "Enviando..." : "Enviar arquivo"}
                    <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploading} />
                  </label>
                </div>
                {filesLoading ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />)}</div>
                ) : fileList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <Folder className="w-8 h-8 mb-2 text-slate-300" />
                    <p className="text-sm">Nenhum arquivo anexado a este negócio.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fileList.map(f => {
                      const ext = f.original_filename?.split(".").pop()?.toLowerCase() || "";
                      const extUpper = ext.toUpperCase();
                      const size = f.size > 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`;
                      const isImage = ["jpg","jpeg","png","gif","webp","svg"].includes(ext);
                      const isPdf = ext === "pdf";
                      const extColors = { pdf: "bg-rose-100 text-rose-600", doc: "bg-blue-100 text-blue-600", docx: "bg-blue-100 text-blue-600", xls: "bg-emerald-100 text-emerald-600", xlsx: "bg-emerald-100 text-emerald-600", csv: "bg-emerald-100 text-emerald-600", ppt: "bg-orange-100 text-orange-600", pptx: "bg-orange-100 text-orange-600", zip: "bg-amber-100 text-amber-600", rar: "bg-amber-100 text-amber-600" };
                      const extStyle = extColors[ext] || "bg-slate-100 text-slate-500";

                      const isLegacy = f.storage_type !== "s3";
                      const openPreview = async () => {
                        if (isLegacy) {
                          toast.error("Este arquivo está em um servidor antigo e não pode ser aberto. Você pode excluí-lo.");
                          return;
                        }
                        setFilePreview({ url: null, filename: f.original_filename, type: f.content_type || "", isImage, isPdf, loading: true });
                        try {
                          const res = await axios.get(`${API}/files/download/${f.id}`, { responseType: "blob" });
                          const url = URL.createObjectURL(res.data);
                          setFilePreview(prev => prev ? { ...prev, url, loading: false } : null);
                        } catch (err) {
                          setFilePreview(null);
                          const msg = err.response?.status === 410
                            ? "Este arquivo está em um servidor antigo e não pode ser aberto."
                            : "Erro ao abrir arquivo.";
                          toast.error(msg);
                        }
                      };

                      return (
                        <div key={f.id} className={`flex items-center gap-3 rounded-lg border p-3 transition-colors group cursor-pointer ${isLegacy ? "border-amber-200 bg-amber-50/30" : "border-slate-200 hover:border-slate-300"}`} onClick={openPreview}>
                          {isImage && !isLegacy ? (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden">
                              <img
                                src={`${API}/files/download/${f.id}`}
                                alt={f.original_filename}
                                className="w-full h-full object-cover"
                                onError={e => { e.target.style.display = "none"; e.target.parentElement.innerHTML = `<span class="flex items-center justify-center w-full h-full text-[10px] font-bold text-slate-500">${extUpper}</span>`; }}
                              />
                            </div>
                          ) : (
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${extStyle}`}>
                              <span className="text-[10px] font-bold">{extUpper}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{f.original_filename}</p>
                            <div className="flex items-center gap-2 text-[11px] text-slate-400">
                              <span>{size}</span>
                              <span>·</span>
                              <span>{f.uploaded_by_name}</span>
                              <span>·</span>
                              <span>{new Date(f.created_at).toLocaleDateString("pt-BR")}</span>
                              {f.doc_type && <><span>·</span><span className="font-medium text-slate-500">{f.doc_type}</span></>}
                              {isLegacy && <><span>·</span><span className="font-medium text-amber-600">Servidor antigo</span></>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                            <Tooltip><TooltipTrigger asChild>
                              <button onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/files/download/${f.id}`, { responseType: "blob" });
                                  const url = URL.createObjectURL(res.data);
                                  const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
                                  URL.revokeObjectURL(url);
                                } catch { toast.error("Erro ao baixar"); }
                              }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger><TooltipContent>Baixar</TooltipContent></Tooltip>
                            <Tooltip><TooltipTrigger asChild>
                              <button onClick={async () => {
                                try { await axios.delete(`${API}/files/${f.id}`); fetchFiles(); toast.success("Arquivo removido"); } catch { toast.error("Erro"); }
                              }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* File Preview Modal */}
                {filePreview && createPortal(
                  <div className="fixed inset-0 z-[99999] bg-black/95 flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 bg-black flex-shrink-0 border-b border-white/10">
                      <div className="flex items-center gap-3 min-w-0">
                        <Folder className="w-4 h-4 text-white/50 flex-shrink-0" />
                        <p className="text-sm text-white/80 truncate">{filePreview.filename}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {filePreview.isImage && (
                          <>
                            <button onClick={() => setPreviewZoom(z => Math.max(25, z - 25))}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                              <ZoomOut className="w-4 h-4" />
                            </button>
                            <span className="text-xs text-white/50 w-10 text-center">{previewZoom}%</span>
                            <button onClick={() => setPreviewZoom(z => Math.min(300, z + 25))}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                              <ZoomIn className="w-4 h-4" />
                            </button>
                            <div className="w-px h-5 bg-white/20 mx-1" />
                          </>
                        )}
                        <button onClick={async () => {
                          if (!filePreview.url) return;
                          try {
                            const a = document.createElement("a"); a.href = filePreview.url; a.download = filePreview.filename; a.click();
                          } catch {}
                        }} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors" disabled={!filePreview.url}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => { if (filePreview.url) URL.revokeObjectURL(filePreview.url); setFilePreview(null); setPreviewZoom(100); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1 overflow-auto flex items-center justify-center" onClick={() => { if (filePreview.url) URL.revokeObjectURL(filePreview.url); setFilePreview(null); setPreviewZoom(100); }}>
                      <div onClick={e => e.stopPropagation()}>
                        {filePreview.loading ? (
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-white/60" />
                            <p className="text-sm text-white/50">Carregando arquivo...</p>
                          </div>
                        ) : filePreview.isImage ? (
                          <img src={filePreview.url} alt={filePreview.filename}
                            style={{ transform: `scale(${previewZoom / 100})`, transformOrigin: "center", transition: "transform 0.2s" }}
                            className="max-w-none" />
                        ) : filePreview.isPdf ? (
                          <iframe src={filePreview.url} title={filePreview.filename} className="w-[100vw] h-[calc(100vh-48px)] bg-white" />
                        ) : (
                          <div className="bg-white rounded-lg p-8 text-center">
                            <Folder className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-sm text-slate-500">Preview não disponível para este tipo de arquivo.</p>
                            <p className="text-xs text-slate-400 mt-1">{filePreview.filename}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Contract Rule Block Modal */}
    <AlertDialog open={contractRuleBlockOpen} onOpenChange={setContractRuleBlockOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600"><AlertCircle className="w-5 h-5" />Contrato pendente</AlertDialogTitle>
          <AlertDialogDescription>{contractRuleBlockMsg}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Entendi</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Isento Confirm */}
    <AlertDialog open={isentoConfirmOpen} onOpenChange={setIsentoConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" style={{ color: tc500 }} />
            {isIsento ? "Remover isenção?" : "Isentar cobrança?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isIsento
              ? "Ao remover a isenção, será necessário emitir ou registrar uma cobrança para avançar ou dar ganho neste negócio."
              : "Ao isentar, este negócio poderá avançar e ser ganho sem a necessidade de emitir ou registrar cobrança."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction className="text-white" style={{ backgroundColor: tc500 }} onClick={async () => {
            const newVal = !isIsento;
            try {
              await axios.post(`${API}/asaas/toggle-isento/${negocio.id}`, { isento: newVal }, { withCredentials: true });
              toast.success(newVal ? "Cobrança isenta!" : "Isenção removida.");
              setLocalIsento(newVal);
              if (onSaved) onSaved();
            } catch (err) { toast.error(err.response?.data?.detail || "Sem permissão"); }
            setIsentoConfirmOpen(false);
          }}>Confirmar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Contract Wizard (from Contrato tab) */}
    {/* Attach Signed Contract Modal */}
    <Dialog open={attachContractOpen} onOpenChange={setAttachContractOpen}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Paperclip className="w-4 h-4" style={{ color: tc500 }} />Anexar contrato assinado</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-slate-500">Suba o arquivo do contrato já assinado pelas partes. Ele será registrado como contrato assinado neste negócio.</p>
          <div>
            <Label className="text-sm font-medium text-slate-700">Nome do documento</Label>
            <Input value={attachName} onChange={e => setAttachName(e.target.value)} placeholder="Ex: Contrato de Prestação de Serviços" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Arquivo (PDF, imagem)</Label>
            <div className="mt-1.5">
              {attachFile ? (
                <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg min-w-0 overflow-hidden">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700 flex-1 truncate min-w-0">{attachFile.name}</span>
                  <button type="button" onClick={() => setAttachFile(null)} className="text-slate-400 hover:text-slate-600 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-colors">
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Clique para selecionar o arquivo</span>
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setAttachFile(f);
                      if (!attachName) setAttachName(f.name.replace(/\.[^.]+$/, ""));
                    }
                  }} />
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setAttachContractOpen(false)}>Cancelar</Button>
          <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!attachFile || !attachName.trim() || attachUploading}
            onClick={async () => {
              setAttachUploading(true);
              try {
                // 1. Upload file to S3
                const formData = new FormData();
                formData.append("file", attachFile);
                formData.append("negocio_id", negocio.id);
                formData.append("category", "contrato");
                const uploadRes = await axios.post(`${API}/files/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
                const fileId = uploadRes.data?.id || uploadRes.data?.file_id;
                if (!fileId) throw new Error("Upload falhou");
                // 2. Attach as signed contract
                await axios.post(`${API}/autentique/contracts/attach-signed`, {
                  negocio_id: negocio.id, file_id: fileId, doc_name: attachName.trim(),
                });
                toast.success("Contrato assinado anexado!");
                setAttachContractOpen(false);
                fetchContracts();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Erro ao anexar contrato.");
              }
              setAttachUploading(false);
            }}>
            {attachUploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Enviando...</> : <><Paperclip className="w-4 h-4 mr-2" />Anexar</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <ContractWizardModal
      open={contractWizardOpen}
      onClose={() => setContractWizardOpen(false)}
      prefillNegocioId={contractWizardData?.negocio_id}
      prefillTemplateId={contractWizardData?.template_id}
      stageTemplates={contractWizardData?.stageTemplates}
      onContractSent={() => {
        toast.success("Contrato emitido com sucesso!");
        fetchContracts();
        setContractWizardOpen(false);
      }}
    />

    <NegocioDialogs ctx={{
      negocio, stages, pipeline, customFields, stageFieldsMap, contatos, onSaved, tc500,
      contatoRecord, empresaRecord,
      duplicateOpen, setDuplicateOpen, handleDuplicate, actionLoading,
      deleteOpen, setDeleteOpen, handleDelete,
      reabrirOpen, setReopenOpen, handleReabrir, reabrindo,
      ganhandoOpen, setGanhandoOpen, handleGanhar, ganhando, setGanhando, ganhoDate, setGanhoDate, ganhoEditing, setGanhoEditing, ganhoConfirmOpen, setGanhoConfirmOpen,
      pendingMoveStageId, setPendingMoveStageId, pendingMoveValues, setPendingMoveValues, savePendingMove,
      taskDialogOpen, setTaskDialogOpen, editingTask, setEditingTask, taskForm, setTaskForm,
      taskTypes, handleCreateTask, translateTaskType,
      concludeTaskTarget, setConcludeTaskTarget, concludeResultado, setConcludeResultado, handleConcludeTask,
      taskDeleteTarget, setTaskDeleteTarget, handleDeleteTask,
      reopenTaskTarget, setReopenTaskTarget, fetchTasks,
      editEmpresaOpen, setEditEmpresaOpen,
      editContatoItem, setEditContatoItem,
      quickCreate, setQuickCreate, setForm,
      noteEditorOpen, setNoteEditorOpen, editingNote, setEditingNote,
      noteDeleteTarget, setNoteDeleteTarget, fetchNotes,
      historyDetailEntry, setHistoryDetailEntry,
      emailComposerOpen, setEmailComposerOpen, emailDetailItem, setEmailDetailItem, fetchEmails,
      aiContextOpen, setAiContextOpen, aiContextText, aiContextLoading,
      quickWaOpen, setQuickWaOpen, quickWaText, setQuickWaText, quickWaSending, handleQuickWaSend,
      perderOpen, setPerderOpen, motivos, selectedMotivo, setSelectedMotivo, handlePerder, perdendo,
      viewAllOpen, setViewAllOpen,
    }} />
    {/* Phone number picker modal */}
    <Dialog open={phonePickerOpen} onOpenChange={setPhonePickerOpen}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Phone className="w-4 h-4" style={{ color: "#f97316" }} />Escolha o número</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {phonePickerNumbers.map((p, i) => (
            <button key={i} onClick={() => {
              window.dispatchEvent(new CustomEvent("pipe4:dial", { detail: { phone: p.valor, negocio_id: negocio?.id } }));
              setPhonePickerOpen(false);
            }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-colors text-left">
              <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">{applyMask(p.valor, appearance.phone_mask || "(##) #####-####")}</p>
                <p className="text-[10px] text-slate-400">{p.tipo}</p>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
